import React, { useState, useEffect, useCallback } from 'react';
import { csvCell } from '../../lib/csvCell';
import { searchText } from '../../lib/piiEnvelope';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { buildApplicantPdfFileName } from '../../lib/pdfFileName';
import SearchableSelect from '../shared/SearchableSelect';
import Modal from '../shared/Modal';
import { db, auth, createAuditLog } from '../../lib/firebase';
import { collection, query, orderBy, getDocs, setDoc, doc, deleteDoc, addDoc } from 'firebase/firestore';
import {
  FinanceProduct, InvoiceRecord, ReceiptRecord, FinanceDashboardStats, Application, WorkspaceBranding,
  QuoteRecord, FinanceReminderRule, FinancialAuditLogRecord
} from '../../types';
import InvoiceBuilderModal from './InvoiceBuilderModal';
import InvoicePreviewModal from './InvoicePreviewModal';
import InvoiceTemplateModal from './InvoiceTemplateModal';
import QuoteBuilderModal from './QuoteBuilderModal';
import QuotePreviewModal from './QuotePreviewModal';
import CommissionsManager from './CommissionsManager';
import { TiptapEditor } from './TiptapEditor';
import {
  DollarSign, FileSpreadsheet, Package, Receipt, Plus, Search, Filter, Download, Upload, Paperclip,
  Eye, Edit3, Trash2, Mail, CheckCircle2, AlertTriangle, AlertCircle, Clock, RefreshCw, X, Loader,
  TrendingUp, ArrowUpRight, ArrowDownRight, CreditCard, Sparkles, Check, Settings,
  ChevronLeft, ChevronRight, FileText, Bell, BarChart3, History, ArrowRight, ShieldCheck,
  CheckSquare, ExternalLink, BadgeDollarSign
} from 'lucide-react';
import { getSafeApplicantName } from '../../lib/nameUtils';
import { safeFetch, downloadPdf, triggerBlobDownload } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog, promptDialog } from '../../lib/confirmStore';
import { workspaceHeader } from '../../lib/workspaces';

const wsFetch = (url: string, options?: RequestInit): Promise<Response> => {
  return fetch(url, {
    ...options,
    headers: { ...workspaceHeader(), ...(options?.headers as Record<string, string>) },
  });
};


interface FinanceTabProps {
  applications: Application[];
  branding?: WorkspaceBranding;
  onViewApplicant?: (app: Application) => void;
  canEdit?: boolean;
}

export default function FinanceTab({ applications, branding, onViewApplicant, canEdit = true }: FinanceTabProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'quotes' | 'invoices' | 'commissions' | 'catalogue' | 'receipts' | 'payment-links' | 'reminders' | 'reports' | 'audit'>('overview');
  const [paymentLinks, setPaymentLinks] = useState<any[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);

  // Quotes state
  const [quotes, setQuotes] = useState<QuoteRecord[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [quoteBuilderOpen, setQuoteBuilderOpen] = useState(false);
  const [quoteToEdit, setQuoteToEdit] = useState<QuoteRecord | null>(null);
  const [selectedQuoteForPreview, setSelectedQuoteForPreview] = useState<QuoteRecord | null>(null);

  // Reminders state
  const [reminderRules, setReminderRules] = useState<FinanceReminderRule[]>([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [savingReminders, setSavingReminders] = useState(false);
  const [processingReminders, setProcessingReminders] = useState(false);

  // Reports state
  const [reportData, setReportData] = useState<any>(null);
  const [loadingReports, setLoadingReports] = useState(false);

  // Name catalogs used to render human-readable Program/Sponsor filter options
  // instead of raw record ids (programId/sponsorId). Loaded lazily on mount.
  const [programCatalog, setProgramCatalog] = useState<any[]>([]);
  const [sponsorCatalog, setSponsorCatalog] = useState<any[]>([]);

  const isLikelyRecordId = (v?: string) => !v || /^(prog|prg|pr|program|spon|sponsor|sp|org|partner|org_|sp_)[_-]/i.test(v);

  const programNameById = new Map<string, string>();
  (programCatalog || []).forEach((p: any) => {
    const name = p?.name || '';
    if (!name) return;
    if (p?.id) programNameById.set(String(p.id).toLowerCase(), name);
    if (p?.programId) programNameById.set(String(p.programId).toLowerCase(), name);
  });
  const sponsorNameById = new Map<string, string>();
  (sponsorCatalog || []).forEach((s: any) => {
    const name = s?.name || '';
    if (!name) return;
    if (s?.sponsorId) sponsorNameById.set(String(s.sponsorId).toLowerCase(), name);
    if (s?.id) sponsorNameById.set(String(s.id).toLowerCase(), name);
  });

  const resolveProgramLabel = (a: any): string => {
    const direct = a?.programName || a?.programType || '';
    if (direct && !isLikelyRecordId(direct)) return direct;
    const mapped = programNameById.get(String(a?.programId || '').toLowerCase());
    return mapped || direct || a?.programId || '';
  };
  const resolveSponsorLabel = (a: any): string => {
    const direct = a?.sponsorName || '';
    if (direct && !isLikelyRecordId(direct)) return direct;
    const mapped = sponsorNameById.get(String(a?.sponsorId || '').toLowerCase());
    return mapped || direct || a?.sponsorId || '';
  };

  const availablePrograms = Array.from(new Set((applications || []).map(resolveProgramLabel).filter(Boolean)));
  const availableTerms = Array.from(new Set(applications.map(a => a.term).filter(Boolean)));
  const appSponsorNames = Array.from(new Set((applications || []).map(resolveSponsorLabel).filter(Boolean)));
  // Prefer sponsors actually linked to workspace applicants, but when the
  // sponsor catalog is available include every named sponsor/partner so the
  // dropdown mirrors the /sponsors list (not raw ids).
  const catalogSponsorNames = (sponsorCatalog || [])
    .map((s: any) => s?.name || '')
    .filter((n: string) => n && !isLikelyRecordId(n));
  const availableSponsors = Array.from(new Set(
    appSponsorNames.length ? [...appSponsorNames, ...catalogSponsorNames] : catalogSponsorNames
  ));

  const [reportPrograms, setReportPrograms] = useState<string[]>([]);
  const [reportTerms, setReportTerms] = useState<string[]>([]);
  const [reportSponsors, setReportSponsors] = useState<string[]>([]);
  const [reportReferrer, setReportReferrer] = useState('');

  // Audit log state
  const [auditLogs, setAuditLogs] = useState<FinancialAuditLogRecord[]>([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);

  const fetchQuotes = useCallback(async () => {
    setLoadingQuotes(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await safeFetch('/api/admin/finance/quotes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuotes(res.quotes || []);
    } catch (e: any) {
      console.warn('Failed to fetch quotes:', e.message);
    } finally {
      setLoadingQuotes(false);
    }
  }, []);

  const fetchReminders = useCallback(async () => {
    setLoadingReminders(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await safeFetch('/api/admin/finance/reminders', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReminderRules(res.settings?.rules || []);
    } catch (e: any) {
      console.warn('Failed to fetch reminders:', e.message);
    } finally {
      setLoadingReminders(false);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    setLoadingReports(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const params = new URLSearchParams();
      if (reportPrograms.length) params.set('program', reportPrograms.join(','));
      if (reportTerms.length) params.set('term', reportTerms.join(','));
      if (reportSponsors.length) params.set('sponsor', reportSponsors.join(','));
      if (reportReferrer) params.set('referrer', reportReferrer);

      const res = await safeFetch(`/api/admin/finance/reports?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReportData(res);
    } catch (e: any) {
      console.warn('Failed to fetch reports:', e.message);
    } finally {
      setLoadingReports(false);
    }
  }, [reportPrograms, reportTerms, reportSponsors, reportReferrer]);

  const fetchAuditLogs = useCallback(async () => {
    setLoadingAuditLogs(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await safeFetch('/api/admin/finance/audit-logs', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAuditLogs(res.auditLogs || []);
    } catch (e: any) {
      console.warn('Failed to fetch audit logs:', e.message);
    } finally {
      setLoadingAuditLogs(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'quotes') fetchQuotes();
    if (activeTab === 'reminders') fetchReminders();
    if (activeTab === 'reports') fetchReports();
    if (activeTab === 'audit') fetchAuditLogs();
  }, [activeTab, fetchQuotes, fetchReminders, fetchReports, fetchAuditLogs]);

  // Lazy-load program & sponsor name catalogs so the Reports filters can show
  // human-readable names instead of raw ids. Failures degrade silently to the
  // name fields already present on application/finance records.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const [progRes, sponsRes] = await Promise.allSettled([
          safeFetch('/api/admin/programs', { headers: { Authorization: `Bearer ${token}` } }),
          safeFetch('/api/admin/sponsors', { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (cancelled) return;
        if (progRes.status === 'fulfilled') {
          const list = (progRes.value as any)?.programs;
          if (Array.isArray(list)) setProgramCatalog(list);
        }
        if (sponsRes.status === 'fulfilled') {
          const list = (sponsRes.value as any)?.sponsors;
          if (Array.isArray(list)) setSponsorCatalog(list);
        }
      } catch (e: any) {
        console.warn('Failed to load report name catalogs:', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchPaymentLinks = useCallback(async () => {
    setLoadingLinks(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await safeFetch('/api/admin/finance/payment-links', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPaymentLinks(res.paymentLinks || []);
    } catch (e: any) {
      console.warn('Failed to fetch payment links:', e.message);
    } finally {
      setLoadingLinks(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'payment-links') {
      fetchPaymentLinks();
    }
  }, [activeTab, fetchPaymentLinks]);

  // Helper to resolve application record for modal preview
  const handleApplicantClick = (applicantId?: string, applicantEmail?: string) => {
    if (!onViewApplicant) return;
    const match = applications.find(a =>
      (applicantId && a.applicationId === applicantId) ||
      (applicantId && a.id === applicantId) ||
      (applicantEmail && a.userEmail?.toLowerCase() === applicantEmail.toLowerCase())
    );
    if (match) onViewApplicant(match);
  };

  // Core Data States
  const [invoices, setInvoices] = useState<InvoiceRecord[]>([]);
  const [products, setProducts] = useState<FinanceProduct[]>([]);
  const [receipts, setReceipts] = useState<ReceiptRecord[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [credits, setCredits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [programFilter, setProgramFilter] = useState('ALL');

  // Modals
  const [isInvoiceBuilderOpen, setIsInvoiceBuilderOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<InvoiceRecord | null>(null);

  const [previewInvoice, setPreviewInvoice] = useState<InvoiceRecord | null>(null);
  const [selectedPaymentDetail, setSelectedPaymentDetail] = useState<any | null>(null);

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<FinanceProduct | null>(null);
  const [prodSearch, setProdSearch] = useState('');
  const [prodCategoryFilter, setProdCategoryFilter] = useState('All');
  const [prodStatusFilter, setProdStatusFilter] = useState('All');
  const [isProductImportModalOpen, setIsProductImportModalOpen] = useState(false);
  const [importCsvFile, setImportCsvFile] = useState<File | null>(null);
  const [importingProducts, setImportingProducts] = useState(false);

  const [isRecordPaymentOpen, setIsRecordPaymentOpen] = useState(false);
  const [paymentTargetInvoice, setPaymentTargetInvoice] = useState<InvoiceRecord | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Credit Card / Stripe');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentReceiptFile, setPaymentReceiptFile] = useState<File | null>(null);
  const [emailReceiptToApplicant, setEmailReceiptToApplicant] = useState(true);
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);

  // Pagination States
  const [invoicePage, setInvoicePage] = useState(1);
  const [receiptPage, setReceiptPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Finance Settings & Templates States
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [billingEmail, setBillingEmail] = useState('');
  const [invoiceFooter, setInvoiceFooter] = useState('');
  const [defaultTerms, setDefaultTerms] = useState('Net 14 Days');
  const [workspaceCurrency, setWorkspaceCurrency] = useState('USD');
  const [lateFeeDefaultEnabled, setLateFeeDefaultEnabled] = useState(false);
  const [lateFeeDefaultType, setLateFeeDefaultType] = useState<'percentage' | 'fixed'>('percentage');
  const [lateFeeDefaultValue, setLateFeeDefaultValue] = useState(5);
  const [lateFeeDefaultDays, setLateFeeDefaultDays] = useState(7);
  const [savingSettings, setSavingSettings] = useState(false);

  const safeNum = (val: any): number => {
    const n = Number(val);
    return isNaN(n) ? 0 : n;
  };
  const fmtMoney = (val: any): string => {
    return safeNum(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const fmtCurrencySymbol = (): string => {
    const symbols: Record<string, string> = {
      USD: '$', EUR: '€', GBP: '£', CAD: '$', AUD: '$', ZAR: 'R', NGN: '₦', KES: 'KSh', GHS: '₵', INR: '₹',
    };
    return symbols[workspaceCurrency] || '$';
  };
  const round2 = (n: number): number => Math.round(n * 100) / 100;

  // Payment Pending Review → Approve & Allocate flow
  const [isAllocationModalOpen, setIsAllocationModalOpen] = useState(false);
  const [pendingPaymentToAllocate, setPendingPaymentToAllocate] = useState<any | null>(null);
  const [pendingEmailPrompt, setPendingEmailPrompt] = useState<{
    isOpen: boolean;
    title: string;
    recipientEmail: string;
    attachmentsInfo: string;
    onSendNow: () => void;
    onSkipEmail: () => void;
  } | null>(null);
  const [openInvoicesForAllocation, setOpenInvoicesForAllocation] = useState<InvoiceRecord[]>([]);
  const [allocationInputs, setAllocationInputs] = useState<Record<string, number>>({});
  const [allocating, setAllocating] = useState(false);

  const isPendingPayment = (r: any): boolean => {
    if (!r) return false;
    const st = String(r.status || '').toLowerCase().trim();
    if (['approved', 'confirmed', 'rejected', 'voided', 'void', 'refunded', 'deleted'].includes(st)) {
      return false;
    }
    return ['pending', 'pending approval', 'pending review', 'awaiting approval', 'in review'].includes(st);
  };

  const [templates, setTemplates] = useState<any[]>([]);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<any | null>(null);

  // Fetch Finance Settings
  const fetchFinanceSettings = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/settings', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const settings = data.financeSettings || {};
        setBillingEmail(settings.billingEmail || branding?.supportEmail || 'billing@isats.org');
        setInvoiceFooter(settings.invoiceFooter || '');
        setDefaultTerms(settings.defaultTerms || 'Net 14 Days');
        setWorkspaceCurrency(settings.currency || 'USD');
        setLateFeeDefaultEnabled(settings.lateFeeDefaultEnabled ?? false);
        setLateFeeDefaultType(settings.lateFeeDefaultType || 'percentage');
        setLateFeeDefaultValue(settings.lateFeeDefaultValue ?? 5);
        setLateFeeDefaultDays(settings.lateFeeDefaultDays ?? 7);
      }
    } catch (e) {
      console.error('Failed to fetch finance settings:', e);
    }
  }, [branding]);

  // Fetch Invoice Templates
  const fetchTemplates = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/invoice-templates', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (e) {
      console.error('Failed to fetch invoice templates:', e);
    }
  }, []);

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          billingEmail,
          invoiceFooter,
          defaultTerms,
          currency: workspaceCurrency,
          lateFeeDefaultEnabled,
          lateFeeDefaultType,
          lateFeeDefaultValue,
          lateFeeDefaultDays,
        }),
      });
      if (!res.ok) throw new Error('Failed to save settings');
      notifyToast('success', 'Finance settings saved successfully.');
      setIsSettingsOpen(false);
    } catch (err: any) {
      notifyToast('error', err.message || 'Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveTemplatePayload = async (templateData: any) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/invoice-templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(templateData),
      });
      if (!res.ok) throw new Error('Failed to save template');
      await fetchTemplates();
      setIsTemplateModalOpen(false);
      setEditingTemplate(null);
    } catch (err: any) {
      notifyToast('error', err.message || 'Error saving template');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!(await confirmDialog('Are you sure you want to delete this template? This cannot be undone.'))) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      await wsFetch(`/api/admin/finance/invoice-templates/${templateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchTemplates();
    } catch (err: any) {
      notifyToast('error', 'Failed to delete template');
    }
  };

  // Fetch Invoices
  const fetchInvoices = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/invoices', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices || []);
        return;
      }
    } catch (e) {
      console.warn("Server fetch invoices error, falling back to Firestore client SDK:", e);
    }
    try {
      const q = query(collection(db, 'invoices'), orderBy('uploadedAt', 'desc'));
      const snap = await getDocs(q);
      const list: InvoiceRecord[] = snap.docs.map(d => ({ id: d.id, ...d.data() } as InvoiceRecord));
      setInvoices(list);
    } catch (err) {
      console.warn("Could not fetch invoices from Firestore", err);
    }
  }, []);

  // Fetch Products
  const fetchProducts = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/products', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
        return;
      }
    } catch (e) {
      console.warn("Server fetch products error, falling back to Firestore client SDK:", e);
    }
    try {
      const q = query(collection(db, 'financeProducts'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const list: FinanceProduct[] = snap.docs.map(d => ({ id: d.id, ...d.data() } as FinanceProduct));
      setProducts(list);
    } catch (err) {
      console.warn("Could not fetch finance products", err);
    }
  }, []);

  // Fetch Receipts / Payments
  const fetchReceipts = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/receipts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setReceipts(data.receipts || []);
        return;
      }
    } catch (e) {
      console.warn("Server fetch receipts error, falling back to Firestore client SDK:", e);
    }
    try {
      const q = query(collection(db, 'receipts'), orderBy('uploadedAt', 'desc'));
      const snap = await getDocs(q);
      const list: ReceiptRecord[] = snap.docs.map(d => ({ receiptId: d.id, ...d.data() } as ReceiptRecord));
      setReceipts(list);
    } catch (err) {
      console.warn("Could not fetch receipts", err);
    }
  }, []);

  // Fetch Payments
  const fetchPayments = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/payments', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPayments(data.payments || []);
        return;
      }
    } catch (e) {
      console.warn("Server fetch payments error:", e);
    }
    try {
      const q = query(collection(db, 'payments'), orderBy('paymentDate', 'desc'));
      const snap = await getDocs(q);
      setPayments(snap.docs.map(d => ({ id: d.id, paymentId: d.id, ...d.data() } as any)));
    } catch (err) {
      console.warn("Could not fetch payments", err);
    }
  }, []);

  // Fetch Applicant Credits
  const fetchCredits = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/credits', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCredits(data.credits || []);
        return;
      }
    } catch (e) {
      console.warn("Server fetch credits error:", e);
    }
    setCredits([]);
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchInvoices(), fetchProducts(), fetchReceipts(), fetchPayments(), fetchCredits(), fetchFinanceSettings(), fetchTemplates()]);
    setLoading(false);
  }, [fetchInvoices, fetchProducts, fetchReceipts, fetchPayments, fetchCredits, fetchFinanceSettings, fetchTemplates]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Dashboard Stats Calculations
  const totalInvoiced = invoices.reduce((sum, inv) => sum + (inv.status !== 'Cancelled' ? safeNum(inv.total ?? inv.amount) : 0), 0);
  const totalCollected = invoices.reduce((sum, inv) => sum + (inv.status !== 'Cancelled' ? safeNum(inv.amountPaid) : 0), 0);
  const totalOutstanding = invoices.reduce((sum, inv) => sum + (inv.status !== 'Cancelled' ? safeNum(inv.amountDue ?? (inv.total ?? inv.amount)) : 0), 0);
  const totalOverdue = invoices.filter(inv => inv.status === 'Overdue').reduce((sum, inv) => sum + safeNum(inv.amountDue ?? (inv.total ?? inv.amount)), 0);
  const totalUnappliedCredit = payments.reduce((sum, p) => sum + safeNum(p.unappliedBalance ?? (safeNum(p.amount) - safeNum(p.totalAllocated))), 0);

  // Filtered Invoices
  const filteredInvoices = invoices.filter(inv => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchName = inv.applicantName?.toLowerCase().includes(q);
      const matchEmail = inv.applicantEmail?.toLowerCase().includes(q);
      const matchNum = inv.invoiceNumber?.toLowerCase().includes(q);
      if (!matchName && !matchEmail && !matchNum) return false;
    }
    if (statusFilter !== 'ALL' && inv.status !== statusFilter) return false;
    if (programFilter !== 'ALL' && inv.programName !== programFilter) return false;
    return true;
  });

  // Save / Update Invoice Handler
  const handleSaveInvoice = async (invoiceData: Partial<InvoiceRecord>) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(invoiceData),
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || 'Failed to save invoice.');
      }

      await fetchInvoices();
    } catch (err: any) {
      console.error(err);
      throw err;
    }
  };

  // Record Payment Handler
  const handleRecordPaymentSubmit = async () => {
    if (!paymentTargetInvoice || !paymentAmount) return;
    const parsedAmount = parseFloat(paymentAmount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      notifyToast('error', 'Please enter a valid payment amount greater than $0.00');
      return;
    }
    setIsRecordingPayment(true);
    try {
      const token = await auth.currentUser?.getIdToken();

      let receiptAttachment: any = null;
      if (paymentReceiptFile) {
        if (paymentReceiptFile.size > 10 * 1024 * 1024) {
          throw new Error('Receipt attachment must be under 10MB.');
        }
        const base64Content = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1] || result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(paymentReceiptFile);
        });
        receiptAttachment = {
          fileName: paymentReceiptFile.name,
          mimeType: paymentReceiptFile.type || 'application/octet-stream',
          base64Content,
        };
      }

      const res = await wsFetch(`/api/admin/finance/invoices/${paymentTargetInvoice.id || paymentTargetInvoice.invoiceId}/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: parsedAmount,
          paymentMethod,
          paymentReference,
          notes: paymentNotes,
          receiptAttachment,
          emailReceipt: emailReceiptToApplicant,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || 'Failed to record payment');
      }

      notifyToast('success', `Payment of $${parsedAmount.toFixed(2)} recorded successfully.`);
      setIsRecordPaymentOpen(false);
      setPaymentTargetInvoice(null);
      setPaymentAmount('');
      setPaymentReference('');
      setPaymentNotes('');
      setPaymentReceiptFile(null);
      await fetchInvoices();
      await fetchReceipts();
    } catch (err: any) {
      notifyToast('error', err.message || 'Payment recording failed');
    } finally {
      setIsRecordingPayment(false);
    }
  };

  // Email Invoice Handler
  const handleSendInvoiceEmail = async (invoiceId: string) => {
    const token = await auth.currentUser?.getIdToken();
    const res = await wsFetch(`/api/admin/finance/invoices/${invoiceId}/email`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Failed to dispatch invoice email');
    }
  };

  // Open the Approve & Allocate modal for a pending payment. The applicant's
  // open invoices are listed greatest-balance first, pre-filled so the payment
  // amount is spread down the list; the admin can edit each amount independently.
  const openApproveAllocation = (pay: any) => {
    const appId = pay.applicantId;
    const openInvoices = invoices
      .filter(inv =>
        inv.applicantId === appId &&
        !['Paid', 'Cancelled', 'Written Off', 'Void', 'Draft'].includes(inv.status || '') &&
        safeNum(inv.amountDue) > 0
      )
      .sort((a, b) => safeNum(b.amountDue) - safeNum(a.amountDue));

    let remaining = Math.max(0, safeNum(pay.amount));
    const allocs: Record<string, number> = {};
    openInvoices.forEach(inv => {
      const key = inv.id || inv.invoiceId;
      if (remaining <= 0.001) {
        allocs[key] = 0;
      } else {
        const amt = round2(Math.min(remaining, safeNum(inv.amountDue)));
        allocs[key] = amt;
        remaining = round2(remaining - amt);
      }
    });

    setPendingPaymentToAllocate(pay);
    setOpenInvoicesForAllocation(openInvoices);
    setAllocationInputs(allocs);
    setIsAllocationModalOpen(true);
  };

  const handleApprovePayment = (payId: string) => {
    const pay = receipts.find(r => r.receiptId === payId || r.id === payId);
    if (pay) {
      openApproveAllocation(pay);
      return;
    }
    const p = payments.find(x => (x.paymentId || x.id) === payId);
    if (p) {
      openApproveAllocation({ ...p, receiptId: p.paymentId || p.id, applicantName: p.applicantName, applicantEmail: p.applicantEmail });
      return;
    }
    notifyToast('error', 'Payment record not found');
  };

  const submitApproveWithAllocations = async (withAllocations: boolean, sendEmail: boolean = true) => {
    const pay = pendingPaymentToAllocate;
    if (!pay) return;
    setAllocating(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const allocations = withAllocations
        ? openInvoicesForAllocation
            .map(inv => {
              const key = inv.id || inv.invoiceId;
              return { invoiceId: key, amount: Number(allocationInputs[key]) || 0 };
            })
            .filter(a => a.amount > 0)
        : [];
      const res = await wsFetch(`/api/admin/finance/payments/${pay.receiptId || pay.paymentId || pay.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ allocations, sendEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to approve payment');
      const allocCount = (data.allocations || []).length;
      notifyToast("success", allocCount > 0
        ? `Payment approved and applied to ${allocCount} invoice(s). Any unallocated remainder is held as applicant credit.`
        : 'Payment approved. The full amount is held as unapplied applicant credit.');

      const targetId = pay.receiptId || pay.paymentId || pay.id;
      setReceipts(prev => prev.filter(r => (r.receiptId || r.paymentId || r.id) !== targetId));
      setPayments(prev => prev.filter(p => (p.paymentId || p.receiptId || p.id) !== targetId));

      loadAllData();
      setSelectedPaymentDetail(null);
      setIsAllocationModalOpen(false);
      setPendingEmailPrompt(null);
    } catch (err: any) {
      notifyToast('error', err.message || 'Error approving payment');
    } finally {
      setAllocating(false);
    }
  };

  const handleRejectPayment = async (payId: string) => {
    const reason = await promptDialog('Please enter a clear reason for rejecting this payment submission:');
    if (!reason || !reason.trim()) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/payments/${payId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rejectionReason: reason.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to reject payment');
      notifyToast('success', 'Payment submission rejected.');

      setReceipts(prev => prev.filter(r => (r.receiptId || r.paymentId || r.id) !== payId));
      setPayments(prev => prev.filter(p => (p.paymentId || p.receiptId || p.id) !== payId));

      loadAllData();
      setSelectedPaymentDetail(null);
    } catch (err: any) {
      notifyToast('error', err.message || 'Error rejecting payment');
    }
  };

  const handleDisassociateAllocation = async (allocationId: string) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/allocations/${allocationId}/disassociate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to disassociate payment allocation');
      notifyToast('success', 'Payment allocation disassociated successfully.');
      loadAllData();
      if (previewInvoice) {
        const invRes = await wsFetch('/api/admin/finance/invoices', { headers: { Authorization: `Bearer ${token}` } });
        if (invRes.ok) {
          const invData = await invRes.json();
          const found = (invData.invoices || []).find((i: any) => i.id === previewInvoice.id || i.invoiceId === previewInvoice.id);
          if (found) setPreviewInvoice(found);
        }
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Error disassociating allocation');
    }
  };

  // Remove a payment from its invoice WITHOUT deleting the payment record.
  const handleRemovePaymentFromInvoice = async (paymentId: string) => {
    const p = payments.find(x => (x.paymentId || x.id) === paymentId);
    const confirmMsg = p && safeNum(p.totalAllocated) > 0
      ? `Remove this payment ($${fmtMoney(p.amount)}) from its invoice(s)?\n\nThe payment record will be KEPT and the funds will become unapplied credit on the applicant's account.`
      : 'Remove this payment from its invoice? The payment record will be kept.';
    if (!(await confirmDialog(confirmMsg))) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/payments/${paymentId}/remove-from-invoice`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to remove payment from invoice');
      notifyToast('success', data.message || 'Payment removed from invoice and returned to unapplied credit.');
      loadAllData();
      setSelectedPaymentDetail(null);
    } catch (err: any) {
      notifyToast('error', err.message || 'Error removing payment from invoice');
    }
  };

  // Permanently delete a payment record (allocations reversed, receipts removed).
  const handleDeletePayment = async (paymentId: string) => {
    const keyMatch = (x: any) => [x.paymentId, x.receiptId, x.id].some((k: any) => String(k || '') === String(paymentId));
    const p = payments.find(keyMatch);
    const r = receipts.find(keyMatch);
    const rec = p || r;
    const isPending = rec && isPendingPayment(rec);
    const confirmMsg = rec && !isPending && safeNum(rec.totalAllocated) > 0
      ? `PERMANENTLY DELETE payment #${paymentId} ($${fmtMoney(rec.amount)})?\n\nThis will also reverse its allocation(s) (invoice balances updated) and remove its receipts. This CANNOT be undone.`
      : isPending
        ? `PERMANENTLY DELETE pending payment #${paymentId} ($${fmtMoney(rec.amount)})?\n\nThe proof upload and its record will be removed permanently. No credit is created. This CANNOT be undone.`
        : `PERMANENTLY DELETE payment #${paymentId}? This CANNOT be undone.`;
    if (!(await confirmDialog(confirmMsg))) return;

    // Optimistically drop the row so the Pending Payments panel reflects the
    // change immediately; restore both lists if the server rejects the delete.
    const prevReceipts = receipts;
    const prevPayments = payments;
    const matchesKey = (x: any) => [x.paymentId, x.receiptId, x.id].some((k: any) => String(k || '') === paymentId);
    setReceipts(prev => prev.filter(r => !matchesKey(r)));
    setPayments(prev => prev.filter(p => !matchesKey(p)));
    setSelectedPaymentDetail(null);

    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/payments/${paymentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setReceipts(prevReceipts);
        setPayments(prevPayments);
        throw new Error(data.error || 'Failed to delete payment');
      }
      notifyToast('success', data.message || 'Payment record deleted.');
      loadAllData();
    } catch (err: any) {
      setReceipts(prevReceipts);
      setPayments(prevPayments);
      notifyToast('error', err.message || 'Error deleting payment');
    }
  };

  // Apply an applicant credit toward an invoice.
  const handleApplyCredit = async (creditId: string) => {
    const credit = credits.find(c => c.creditId === creditId || c.id === creditId);
    if (!credit) return;
    const targetInvoice = await promptDialog(
      `Enter the invoice number to apply $${fmtMoney(credit.availableBalance)} of credit to:\n\n(Applicant: ${credit.applicantName || credit.applicantEmail || credit.applicantId})`
    );
    if (!targetInvoice || !targetInvoice.trim()) return;
    const inv = invoices.find(i => String(i.invoiceNumber) === targetInvoice.trim());
    if (!inv) {
      notifyToast("error", `Invoice #${targetInvoice} not found.`)
      return;
    }
    const amountStr = await promptDialog(`Credit available: $${fmtMoney(credit.availableBalance)}\nAmount to apply (default: full balance):`);
    if (amountStr === null) return;
    const amount = amountStr.trim() === '' ? safeNum(credit.availableBalance) : Number(amountStr);
    if (isNaN(amount) || amount <= 0) {
      notifyToast('error', 'Please enter a valid amount greater than zero.');
      return;
    }
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/credits/${creditId}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invoiceId: inv.id || inv.invoiceId, amount })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to apply credit');
      notifyToast("success", `Credit of $${amount.toFixed(2)} applied to Invoice #${inv.invoiceNumber}.`)
      loadAllData();
    } catch (err: any) {
      notifyToast('error', err.message || 'Error applying credit');
    }
  };

  // Product Catalogue Handlers
  const handleSaveProduct = async (productData: Partial<FinanceProduct>) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(productData),
      });
      if (!res.ok) throw new Error('Failed to save product');
      setIsProductModalOpen(false);
      setEditingProduct(null);
      await fetchProducts();
    } catch (err: any) {
      notifyToast('error', err.message || 'Product save failed');
    }
  };

  // Export CSV
  const handleExportInvoicesCsv = () => {
    if (filteredInvoices.length === 0) return;
    const headers = ['Invoice #', 'Applicant Name', 'Email', 'Program', 'Invoice Date', 'Due Date', 'Total Amount', 'Amount Paid', 'Balance Due', 'Status'];
    const rows = filteredInvoices.map(inv => [
      inv.invoiceNumber || '-',
      inv.applicantName || '',
      inv.applicantEmail || '-',
      inv.programName || '',
      inv.invoiceDate || '-',
      inv.dueDate || '-',
      (inv.total || 0).toFixed(2),
      (inv.amountPaid || 0).toFixed(2),
      (inv.amountDue || 0).toFixed(2),
      inv.status || '-',
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.map(csvCell).join(','), ...rows.map(r => r.map(csvCell).join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Workspace_Invoices_Export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Top Header & Navigation Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-black text-slate-900 font-display flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-emerald-600 bg-emerald-50 p-1 rounded-lg" />
              Workspace Financial Management
            </h1>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200">
              Currency: {workspaceCurrency}
            </span>
          </div>
          <p className="text-caption text-slate-500 mt-1">
            Centralized invoicing, payment collection ledgers, and billable product catalogue.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsSettingsOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-xl text-xs transition-all cursor-pointer shadow-2xs"
          >
            <Settings className="h-4 w-4 text-slate-500" /> Settings & Templates
          </button>
          <button
            onClick={loadAllData}
            className="p-2 text-slate-500 hover:bg-slate-100 rounded-xl transition-all cursor-pointer border border-slate-200"
            title="Refresh Data"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs Switcher */}
      <div className="flex border-b border-slate-200 gap-6 text-xs font-bold text-slate-500 overflow-x-auto">
        <button
          onClick={() => setActiveTab('overview')}
          className={`pb-3 transition-colors cursor-pointer border-b-2 whitespace-nowrap ${activeTab === 'overview' ? 'border-brand text-slate-900 font-extrabold' : 'border-transparent hover:text-slate-800'}`}
        >
          Financial Overview
        </button>
        <button
          onClick={() => setActiveTab('quotes')}
          className={`pb-3 transition-colors cursor-pointer border-b-2 flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'quotes' ? 'border-brand text-slate-900 font-extrabold' : 'border-transparent hover:text-slate-800'}`}
        >
          Quotes ({quotes.length})
        </button>
        <button
          onClick={() => setActiveTab('invoices')}
          className={`pb-3 transition-colors cursor-pointer border-b-2 flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'invoices' ? 'border-brand text-slate-900 font-extrabold' : 'border-transparent hover:text-slate-800'}`}
        >
          Master Invoices ({invoices.length})
        </button>
        <button
          onClick={() => setActiveTab('commissions')}
          className={`pb-3 transition-colors cursor-pointer border-b-2 flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'commissions' ? 'border-brand text-slate-900 font-extrabold' : 'border-transparent hover:text-slate-800'}`}
        >
          <BadgeDollarSign className="w-3.5 h-3.5" /> Commissions
        </button>
        <button
          onClick={() => setActiveTab('catalogue')}
          className={`pb-3 transition-colors cursor-pointer border-b-2 flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'catalogue' ? 'border-brand text-slate-900 font-extrabold' : 'border-transparent hover:text-slate-800'}`}
        >
          Product Catalogue ({products.length})
        </button>
        <button
          onClick={() => setActiveTab('receipts')}
          className={`pb-3 transition-colors cursor-pointer border-b-2 flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'receipts' ? 'border-brand text-slate-900 font-extrabold' : 'border-transparent hover:text-slate-800'}`}
        >
          Payment Receipts ({receipts.length})
        </button>
        <button
          onClick={() => setActiveTab('payment-links')}
          className={`pb-3 transition-colors cursor-pointer border-b-2 flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'payment-links' ? 'border-brand text-slate-900 font-extrabold' : 'border-transparent hover:text-slate-800'}`}
        >
          Payment Links ({paymentLinks.length})
        </button>
        <button
          onClick={() => setActiveTab('reminders')}
          className={`pb-3 transition-colors cursor-pointer border-b-2 flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'reminders' ? 'border-brand text-slate-900 font-extrabold' : 'border-transparent hover:text-slate-800'}`}
        >
          <Bell className="w-3.5 h-3.5" /> Reminders
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          className={`pb-3 transition-colors cursor-pointer border-b-2 flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'reports' ? 'border-brand text-slate-900 font-extrabold' : 'border-transparent hover:text-slate-800'}`}
        >
          <BarChart3 className="w-3.5 h-3.5" /> Reports
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          className={`pb-3 transition-colors cursor-pointer border-b-2 flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'audit' ? 'border-brand text-slate-900 font-extrabold' : 'border-transparent hover:text-slate-800'}`}
        >
          <History className="w-3.5 h-3.5" /> Audit Trail
        </button>
      </div>

      {/* TAB 1: FINANCIAL OVERVIEW */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Key Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs space-y-1">
              <div className="text-2xl font-black text-slate-900 font-mono tracking-tight">
                ${totalInvoiced.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 block">Total Invoiced</span>
              <p className="text-[11px] text-slate-500 font-medium">Across {invoices.length} issued invoices</p>
            </div>

            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs space-y-1">
              <div className="text-2xl font-black text-emerald-700 font-mono tracking-tight">
                ${totalCollected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 block">Total Collected</span>
              <p className="text-[11px] text-emerald-700 font-semibold">
                {totalInvoiced > 0 ? `${((totalCollected / totalInvoiced) * 100).toFixed(1)}% collection rate` : '0% collection rate'}
              </p>
            </div>

            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs space-y-1">
              <div className="text-2xl font-black text-slate-900 font-mono tracking-tight">
                ${totalOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 block">Outstanding Balance</span>
              <p className="text-[11px] text-slate-500 font-medium">Pending applicant settlement</p>
            </div>

            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs space-y-1">
              <div className="text-2xl font-black text-purple-700 font-mono tracking-tight">
                ${totalUnappliedCredit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 block">Unapplied Credit</span>
              <p className="text-[11px] text-purple-700 font-medium">Available for allocation</p>
            </div>

            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs space-y-1">
              <div className="text-2xl font-black text-rose-700 font-mono tracking-tight">
                ${totalOverdue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 block">Overdue Invoices</span>
              <p className="text-[11px] text-rose-700 font-medium">
                {invoices.filter(i => i.status === 'Overdue').length} overdue invoices
              </p>
            </div>
          </div>

          {/* Quick Recent Activity */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-extrabold text-slate-800 text-xs uppercase tracking-wider">Recent Invoices Overview</h3>
              <button onClick={() => setActiveTab('invoices')} className="text-xs text-brand font-bold hover:underline cursor-pointer">
                View All Invoices &rarr;
              </button>
            </div>
            <div className="divide-y divide-slate-100 text-xs">
              {invoices.slice(0, 5).map(inv => (
                <div key={inv.id || inv.invoiceId} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="space-y-0.5">
                    <div className="font-bold text-slate-900 flex items-center gap-2">
                      <span>#{inv.invoiceNumber}</span>
                      <span className="text-slate-400 font-normal">&bull;</span>
                      <span>{inv.applicantName}</span>
                    </div>
                    <div className="text-slate-500 text-[11px]">
                      Due: {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '-'} &bull; {inv.programName || 'General Program'}
                    </div>
                  </div>
                  <div className="text-right space-y-1">
                    <div className="font-mono font-bold text-slate-900">${safeNum(inv.total ?? inv.amount).toFixed(2)}</div>
                    <span className={`inline-block px-2.5 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider border ${
                      inv.status === 'Paid' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                      inv.status === 'Overdue' ? 'bg-rose-50 text-rose-800 border-rose-200' :
                      inv.status === 'Partially Paid' ? 'bg-amber-50 text-amber-800 border-amber-200' :
                      'bg-blue-50 text-blue-800 border-blue-200'
                    }`}>
                      {inv.status || 'Draft'}
                    </span>
                  </div>
                </div>
              ))}
              {invoices.length === 0 && (
                <div className="p-8 text-center text-slate-400 text-xs">No invoices created yet.</div>
              )}
            </div>
          </div>

          {/* Available Applicant Credits */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-extrabold text-slate-800 text-xs uppercase tracking-wider">Applicant Credits (Unapplied Payments)</h3>
              <span className="text-xs text-purple-700 font-bold">${fmtMoney(totalUnappliedCredit)} available</span>
            </div>
            <div className="divide-y divide-slate-100 text-xs">
              {credits.filter(c => c.status === 'Available' || c.status === 'Partially Used').slice(0, 8).map(credit => (
                <div key={credit.creditId || credit.id} className="p-4 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                  <div className="space-y-0.5 min-w-0">
                    <div className="font-bold text-slate-900 truncate">{credit.applicantName || credit.applicantEmail || 'Applicant'}</div>
                    <div className="text-slate-500 text-[11px]">
                      <span className="font-mono">#{credit.creditId || credit.id}</span> &bull; Source payment: <span className="font-mono">{credit.sourcePaymentId}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <div className="font-mono font-bold text-purple-700">${fmtMoney(credit.availableBalance)}</div>
                      <span className="text-[11px] text-slate-500">{credit.status === 'Partially Used' ? 'Partially used' : 'Available'}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleApplyCredit(credit.creditId || credit.id)}
                      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg text-[11px] transition-all cursor-pointer shadow-2xs"
                    >
                      Apply to Invoice
                    </button>
                  </div>
                </div>
              ))}
              {credits.filter(c => c.status === 'Available' || c.status === 'Partially Used').length === 0 && (
                <div className="p-8 text-center text-slate-400 text-xs">
                  No unapplied credits. Overpayments and payments removed from invoices appear here automatically.
                </div>
              )}
            </div>
          </div>

          {/* Recent Payments */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-extrabold text-slate-800 text-xs uppercase tracking-wider">Recent Payments</h3>
              <span className="text-xs text-slate-500 font-semibold">{payments.length} total</span>
            </div>
            <div className="divide-y divide-slate-100 text-xs">
              {payments.slice(0, 8).map(p => (
                <div key={p.paymentId || p.id} className="p-4 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                  <div className="space-y-0.5 min-w-0">
                    <div className="font-bold text-slate-900 flex items-center gap-2">
                      <span className="font-mono">#{String(p.paymentId || p.id).toUpperCase()}</span>
                      <span className="text-slate-400 font-normal">&bull;</span>
                      <span className="truncate">{p.applicantName || 'Applicant'}</span>
                    </div>
                    <div className="text-slate-500 text-[11px]">
                      {p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '-'} &bull; {p.paymentMethod || 'Unknown'} &bull;
                      {safeNum(p.totalAllocated) > 0 ? ` Applied: $${fmtMoney(p.totalAllocated)}` : ''}
                      {safeNum(p.unappliedBalance) > 0 ? ` &bull; Credit: $${fmtMoney(p.unappliedBalance)}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setSelectedPaymentDetail(p)}
                      className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-[11px] transition-all cursor-pointer"
                    >
                      View
                    </button>
                    {safeNum(p.totalAllocated) > 0 && (
                      <button
                        type="button"
                        onClick={() => handleRemovePaymentFromInvoice(p.paymentId || p.id)}
                        className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold rounded-lg text-[11px] transition-all cursor-pointer"
                        title="Keep the payment but return its funds to unapplied credit"
                      >
                        Remove from Invoice
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeletePayment(p.paymentId || p.id)}
                      className="px-3 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-900 font-bold rounded-lg text-[11px] transition-all cursor-pointer"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {payments.length === 0 && (
                <div className="p-8 text-center text-slate-400 text-xs">No payments recorded yet.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: MASTER INVOICES LIST */}
      {activeTab === 'invoices' && (
        <div className="space-y-4">
          {/* Controls & Search */}
          <div className="flex flex-col sm:flex-row justify-between gap-3 bg-white p-4 rounded-xl border border-slate-200">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by candidate name, email, or invoice number..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-brand"
              />
            </div>

            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="bg-slate-50 border border-slate-200 text-xs font-semibold rounded-lg px-3 py-2 outline-none"
              >
                <option value="ALL">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Sent">Issued / Sent</option>
                <option value="Partially Paid">Partially Paid</option>
                <option value="Paid">Paid</option>
                <option value="Overdue">Overdue</option>
                <option value="Cancelled">Cancelled</option>
              </select>

              <button
                onClick={handleExportInvoicesCsv}
                className="inline-flex items-center gap-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-xs transition-all cursor-pointer"
              >
                <Download className="h-3.5 w-3.5" /> Export CSV
              </button>
            </div>
          </div>

          {/* Master Invoices Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="p-3">Invoice #</th>
                  <th className="p-3">Candidate</th>
                  <th className="p-3">Program</th>
                  <th className="p-3">Issue / Due Date</th>
                  <th className="p-3 text-right">Total</th>
                  <th className="p-3 text-right">Paid</th>
                  <th className="p-3 text-right">Due</th>
                  <th className="p-3 text-center">Status</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {filteredInvoices.slice((invoicePage - 1) * ITEMS_PER_PAGE, invoicePage * ITEMS_PER_PAGE).map(inv => (
                  <tr key={inv.id || inv.invoiceId} className="hover:bg-slate-50/80 transition-colors">
                    <td className="p-3 font-mono font-bold text-slate-900">
                      #{inv.invoiceNumber}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => handleApplicantClick(inv.applicantId, inv.applicantEmail)}
                        className="text-left font-bold text-slate-900 hover:text-brand hover:underline cursor-pointer group"
                      >
                        <div>{inv.applicantName}</div>
                        <div className="text-[11px] text-slate-500 group-hover:text-blue-500">{inv.applicantEmail}</div>
                      </button>
                    </td>
                    <td className="p-3 text-slate-600">{inv.programName || '-'}</td>
                    <td className="p-3 text-slate-500 text-[11px]">
                      <div>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : '-'}</div>
                      <div className="font-semibold text-slate-700">Due: {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '-'}</div>
                    </td>
                    <td className="p-3 text-right font-mono font-bold text-slate-900">${fmtMoney(inv.total ?? inv.amount)}</td>
                    <td className="p-3 text-right font-mono text-emerald-600">${fmtMoney(inv.amountPaid)}</td>
                    <td className="p-3 text-right font-mono font-bold text-slate-900">${fmtMoney(inv.amountDue ?? (inv.total ?? inv.amount))}</td>
                    <td className="p-3 text-center">
                      <span className={`inline-block px-2.5 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider border ${
                        inv.status === 'Paid' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                        inv.status === 'Partially Paid' ? 'bg-amber-50 text-amber-800 border-amber-200' :
                        inv.status === 'Overdue' ? 'bg-rose-50 text-rose-800 border-rose-200' :
                        inv.status === 'Unpaid' ? 'bg-amber-100 text-amber-900 border-amber-300' :
                        inv.status === 'Sent' ? 'bg-sky-50 text-sky-800 border-sky-200' :
                        inv.status === 'Cancelled' ? 'bg-slate-100 text-slate-500 border-slate-200' :
                        'bg-slate-100 text-slate-700 border-slate-200'
                      }`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="p-3 text-right space-x-1">
                      <button
                        onClick={() => setPreviewInvoice(inv)}
                        className="p-1.5 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-lg cursor-pointer"
                        title="Preview Branded Invoice"
                      >
                        <Eye className="h-4 w-4" />
                      </button>

                      <button
                        onClick={async () => {
                          try {
                            const invId = inv.id || inv.invoiceId;
                            const blob = await downloadPdf(`/api/finance/invoices/${invId}/pdf`);
                            triggerBlobDownload(blob, buildApplicantPdfFileName(`Invoice-${inv.invoiceNumber || invId}`, inv.applicantId || (inv as any).applicationId, inv.applicantName));
                          } catch (e: any) {
                            notifyToast('error', 'Error downloading PDF: ' + e.message);
                          }
                        }}
                        className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg cursor-pointer"
                        title="Download Vector PDF Invoice"
                      >
                        <Download className="h-4 w-4" />
                      </button>

                      <button
                        onClick={async () => {
                          try {
                            const token = await auth.currentUser?.getIdToken();
                            const res = await wsFetch(`/api/admin/finance/invoices/${inv.id || inv.invoiceId}/payment-link`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ action: 'generate' }),
                            });
                            if (!res.ok) throw new Error('Failed to generate payment link');
                            const data = await res.json();
                            if (data.paymentUrl) {
                              await navigator.clipboard.writeText(data.paymentUrl);
                              notifyToast('success', data.existing ? 'Active payment link copied to clipboard' : 'Payment link generated and copied to clipboard');
                              fetchPaymentLinks();
                            }
                          } catch (err: any) {
                            notifyToast('error', err.message || 'Error generating link');
                          }
                        }}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg cursor-pointer"
                        title="Generate / Copy Payment Link"
                      >
                        <Eye className="h-4 w-4 hidden" />
                        <svg className="h-4 w-4 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      </button>

                      {inv.amountDue > 0 && inv.status !== 'Cancelled' && (
                        <button
                          onClick={() => { setPaymentTargetInvoice(inv); setPaymentAmount(inv.amountDue.toString()); setIsRecordPaymentOpen(true); }}
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg cursor-pointer"
                          title="Record Payment"
                        >
                          <CreditCard className="h-4 w-4" />
                        </button>
                      )}

                      <button
                        onClick={() => { setEditingInvoice(inv); setIsInvoiceBuilderOpen(true); }}
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg cursor-pointer"
                        title="Edit Invoice"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>

                      <button
                        onClick={async () => {
                          const invId = inv.id || inv.invoiceId;
                          if (!(await confirmDialog(`Permanently delete Invoice #${inv.invoiceNumber || invId}?\n\nPayments recorded against this invoice will NOT be deleted — active allocations will be reversed and the funds returned to the applicant's unapplied credit pool.`))) return;
                          try {
                            const token = await auth.currentUser?.getIdToken();
                            const res = await wsFetch(`/api/admin/finance/invoices/${invId}`, {
                              method: 'DELETE',
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            const data = await res.json();
                            if (!res.ok) throw new Error(data.error || 'Failed to delete invoice');
                            notifyToast('success', data.message || 'Invoice deleted. Payments preserved and returned to unapplied credit.');
                            await loadAllData();
                          } catch (e: any) {
                            notifyToast('error', e.message);
                          }
                        }}
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg cursor-pointer"
                        title="Delete Invoice"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredInvoices.length === 0 && (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-slate-400">
                      No invoices match the selected criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {filteredInvoices.length > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500 font-medium">
                <div>
                  Showing {((invoicePage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(invoicePage * ITEMS_PER_PAGE, filteredInvoices.length)} of {filteredInvoices.length} invoices
                </div>
                <div className="flex items-center gap-1">
                  <button aria-label="Previous page" title="Previous page"
                    disabled={invoicePage <= 1}
                    onClick={() => setInvoicePage(p => Math.max(1, p - 1))}
                    className="p-1.5 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-30 cursor-pointer"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="px-2 font-bold text-slate-800">Page {invoicePage} of {Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE)}</span>
                  <button aria-label="Next page" title="Next page"
                    disabled={invoicePage >= Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE)}
                    onClick={() => setInvoicePage(p => p + 1)}
                    className="p-1.5 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-30 cursor-pointer"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB: COMMISSIONS */}
      {activeTab === 'commissions' && (
        <CommissionsManager applications={applications} currency={workspaceCurrency} onViewApplicant={onViewApplicant} />
      )}

      {/* TAB 3: PRODUCT CATALOGUE */}
      {activeTab === 'catalogue' && (
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 bg-white p-4 rounded-xl border border-slate-200">
            <div>
              <h3 className="font-extrabold text-slate-900 text-xs uppercase tracking-wider">Billable Products & Services ({products.length})</h3>
              <p className="text-caption text-slate-500">Manage standard item templates and 5-digit product codes for workspace invoices.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  if (products.length === 0) return;
                  const headers = ['productId', 'name', 'category', 'sellingPrice', 'costPrice', 'description', 'isActive'];
                  const rows = products.map(p => [
                    p.productId || Math.floor(10000 + Math.random() * 90000).toString(),
                    p.name || '',
                    p.category || '',
                    p.sellingPrice || 0,
                    p.costPrice || 0,
                    p.description || '',
                    p.isActive !== false ? 'true' : 'false'
                  ]);
                  const csvContent = 'data:text/csv;charset=utf-8,' + [headers.map(csvCell).join(','), ...rows.map(r => r.map(csvCell).join(','))].join('\n');
                  const encodedUri = encodeURI(csvContent);
                  const link = document.createElement('a');
                  link.setAttribute('href', encodedUri);
                  link.setAttribute('download', `products_export_${new Date().toISOString().slice(0, 10)}.csv`);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  notifyToast('success', 'Products exported to CSV');
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-all cursor-pointer"
              >
                <Download className="h-3.5 w-3.5" /> Export CSV
              </button>
              <button
                type="button"
                onClick={() => { setImportCsvFile(null); setIsProductImportModalOpen(true); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-all cursor-pointer"
              >
                <Upload className="h-3.5 w-3.5" /> Import Products
              </button>
              <button
                onClick={() => { setEditingProduct(null); setIsProductModalOpen(true); }}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-brand hover:bg-brand-active text-white font-bold rounded-xl text-xs transition-all cursor-pointer shadow-sm"
              >
                <Plus className="h-3.5 w-3.5" /> Add Product
              </button>
            </div>
          </div>

          {/* Search & Category Filter Bar */}
          <div className="bg-white p-3.5 rounded-xl border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                value={prodSearch}
                onChange={(e) => setProdSearch(e.target.value)}
                placeholder="Search products by 5-digit ID, name, or description..."
                className="w-full pl-9 pr-4 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
              />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <select
                value={prodCategoryFilter}
                onChange={(e) => setProdCategoryFilter(e.target.value)}
                className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white font-semibold text-slate-700 cursor-pointer"
              >
                <option value="All">All Categories</option>
                <option value="Program Fee">Program Fee</option>
                <option value="Visa Fee">Visa Fee</option>
                <option value="Insurance">Insurance</option>
                <option value="Accommodation">Accommodation</option>
                <option value="Flight">Flight</option>
                <option value="Admin Fee">Admin Fee</option>
                <option value="Other">Other</option>
              </select>
              <select
                value={prodStatusFilter}
                onChange={(e) => setProdStatusFilter(e.target.value)}
                className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white font-semibold text-slate-700 cursor-pointer"
              >
                <option value="All">All Statuses</option>
                <option value="Active">Active Only</option>
                <option value="Inactive">Inactive Only</option>
              </select>
            </div>
          </div>

          {/* Product Table View */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-2xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                    <th className="p-3">Product ID</th>
                    <th className="p-3">Name & Details</th>
                    <th className="p-3">Category</th>
                    <th className="p-3 text-right">Selling Price ({fmtCurrencySymbol()})</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {products.filter(prod => {
                    if (prodSearch) {
                      const q = prodSearch.toLowerCase();
                      const pid = (prod.productId || '').toLowerCase();
                      const name = (prod.name || '').toLowerCase();
                      const desc = (prod.description || '').toLowerCase();
                      if (!pid.includes(q) && !name.includes(q) && !desc.includes(q)) return false;
                    }
                    if (prodCategoryFilter !== 'All' && prod.category !== prodCategoryFilter) return false;
                    if (prodStatusFilter === 'Active' && prod.isActive === false) return false;
                    if (prodStatusFilter === 'Inactive' && prod.isActive !== false) return false;
                    return true;
                  }).map(prod => {
                    const displayId = prod.productId || Math.floor(10000 + Math.random() * 90000).toString();
                    return (
                      <tr key={prod.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="p-3 font-mono font-bold text-brand">
                          #{displayId}
                        </td>
                        <td className="p-3">
                          <p className="font-extrabold text-slate-900">{prod.name}</p>
                          <p className="text-[11px] text-slate-500 line-clamp-1">{prod.description || 'No description provided.'}</p>
                        </td>
                        <td className="p-3">
                          <span className="text-[10px] font-black uppercase text-brand tracking-wider bg-blue-50 px-2 py-0.5 rounded-md">
                            {prod.category}
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono font-extrabold text-slate-900 text-sm">
                          {fmtCurrencySymbol()}{safeNum(prod.sellingPrice).toFixed(2)}
                        </td>
                        <td className="p-3 text-center">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${prod.isActive !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`}>
                            {prod.isActive !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => { setEditingProduct({ ...prod, productId: displayId }); setIsProductModalOpen(true); }}
                            className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg cursor-pointer transition-colors"
                            title="Edit Product"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {products.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-400 text-xs">
                        No products in catalogue yet. Click "Add Product" to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* PRODUCT IMPORT MODAL */}
      {isProductImportModalOpen && (
        <Modal onClose={() => setIsProductImportModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4 text-xs animate-fade-in">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-extrabold text-slate-900 text-sm font-display">Import / Update Product Catalogue</h3>
              <button onClick={() => setIsProductImportModalOpen(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="bg-amber-50 border border-amber-200 p-3.5 rounded-xl text-amber-900 space-y-1">
              <p className="font-bold flex items-center gap-1 text-[11px] uppercase tracking-wider text-amber-800">
                <AlertCircle className="h-3.5 w-3.5" /> Important CSV Mapping Notice
              </p>
              <p className="text-[11px] leading-relaxed">
                Please <strong>Export your product list first</strong>, make your edits, and upload the file. Keep the <strong>productId</strong> column intact so existing products are updated without creating duplicates.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase text-slate-500 block">Select Product CSV File</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setImportCsvFile(e.target.files?.[0] || null)}
                className="block w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-blue-50 file:text-brand hover:file:bg-blue-100 cursor-pointer"
              />
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsProductImportModalOpen(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 font-bold rounded-xl cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!importCsvFile || importingProducts}
                onClick={async () => {
                  if (!importCsvFile) return;
                  setImportingProducts(true);
                  try {
                    const reader = new FileReader();
                    reader.onload = async (e) => {
                      try {
                        const text = e.target?.result as string || '';
                        const lines = text.split(/\r?\n/).filter(line => line.trim());
                        if (lines.length <= 1) throw new Error('CSV file is empty or missing headers');

                        const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim());
                        const pidIdx = headers.indexOf('productId');
                        const nameIdx = headers.indexOf('name');
                        const catIdx = headers.indexOf('category');
                        const priceIdx = headers.indexOf('sellingPrice');
                        const costIdx = headers.indexOf('costPrice');
                        const descIdx = headers.indexOf('description');
                        const activeIdx = headers.indexOf('isActive');

                        const token = await auth.currentUser?.getIdToken();

                        for (let i = 1; i < lines.length; i++) {
                          const cols = lines[i].split(',').map(c => c.replace(/^"|"$/g, '').trim());
                          if (!cols[nameIdx >= 0 ? nameIdx : 1]) continue;

                          const pCode = cols[pidIdx >= 0 ? pidIdx : 0] || Math.floor(10000 + Math.random() * 90000).toString();
                          const pName = cols[nameIdx >= 0 ? nameIdx : 1] || 'Product Item';
                          const pCat = cols[catIdx >= 0 ? catIdx : 2] || 'Program Fee';
                          const pPrice = parseFloat(cols[priceIdx >= 0 ? priceIdx : 3] || '0') || 0;
                          const pCost = parseFloat(cols[costIdx >= 0 ? costIdx : 4] || '0') || 0;
                          const pDesc = cols[descIdx >= 0 ? descIdx : 5] || '';
                          const pActive = (cols[activeIdx >= 0 ? activeIdx : 6] || 'true').toLowerCase() !== 'false';

                          // Match existing product by 5-digit productId
                          const matchedProd = products.find(p => p.productId === pCode);
                          const docId = matchedProd ? matchedProd.id : undefined;

                          await wsFetch('/api/admin/finance/products', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            body: JSON.stringify({
                              id: docId,
                              productId: pCode,
                              name: pName,
                              category: pCat,
                              sellingPrice: pPrice,
                              costPrice: pCost,
                              description: pDesc,
                              isActive: pActive
                            })
                          });
                        }

                        notifyToast('success', 'Products imported and synced successfully');
                        setIsProductImportModalOpen(false);
                        setImportCsvFile(null);
                        await fetchProducts();
                      } catch (err: any) {
                        notifyToast('error', err.message || 'Import failed');
                      } finally {
                        setImportingProducts(false);
                      }
                    };
                    reader.readAsText(importCsvFile);
                  } catch (err: any) {
                    notifyToast('error', err.message || 'Failed to read file');
                    setImportingProducts(false);
                  }
                }}
                className="px-5 py-2 bg-brand hover:bg-brand-active text-white font-bold rounded-xl disabled:opacity-50 cursor-pointer shadow-sm"
              >
                {importingProducts ? 'Importing...' : 'Upload & Sync Products'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* TAB 5: PAYMENT LINKS MANAGEMENT */}
      {activeTab === 'payment-links' && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
            <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <span className="font-extrabold text-slate-800 text-xs uppercase tracking-wider block">Generated Payment Links ({paymentLinks.length})</span>
                <span className="text-[11px] text-slate-500">Manage payment links, view status, and manually revoke links (Max 15 days validity)</span>
              </div>
              <button
                type="button"
                onClick={fetchPaymentLinks}
                className="px-3 py-1.5 bg-slate-50 border border-slate-200 hover:bg-slate-100 text-slate-700 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loadingLinks ? 'animate-spin' : ''}`} />
                <span>Refresh Links</span>
              </button>
            </div>

            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="p-3">Payment Token & URL</th>
                  <th className="p-3">Invoice #</th>
                  <th className="p-3">Applicant Name & Email</th>
                  <th className="p-3">Created</th>
                  <th className="p-3">Expires At</th>
                  <th className="p-3 text-center">Status</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {paymentLinks.map(pl => (
                  <tr key={pl.id || pl.token} className="hover:bg-slate-50/80 transition-colors">
                    <td className="p-3 font-mono">
                      <div className="font-bold text-slate-900 truncate max-w-xs">{pl.paymentUrl}</div>
                      <div className="text-[11px] text-slate-500">{pl.token}</div>
                    </td>
                    <td className="p-3 font-bold text-slate-800 font-mono">
                      #{pl.invoiceNumber || pl.invoiceId || '—'}
                    </td>
                    <td className="p-3">
                      <button
                        type="button"
                        onClick={() => handleApplicantClick(pl.applicantId, pl.applicantEmail)}
                        className="text-left font-bold text-slate-900 hover:text-brand hover:underline cursor-pointer group"
                      >
                        <div>{pl.applicantName || 'Applicant'}</div>
                        <div className="text-[11px] text-slate-500 group-hover:text-blue-500">{pl.applicantEmail || 'No email recorded'}</div>
                      </button>
                    </td>
                    <td className="p-3 text-slate-500 text-[11px]">
                      {pl.createdAt ? new Date(pl.createdAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-3 text-slate-500 text-[11px]">
                      {pl.expiresAt ? new Date(pl.expiresAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase border ${
                        pl.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        pl.isExpired ? 'bg-rose-50 text-rose-700 border-rose-200' :
                        'bg-slate-100 text-slate-500 border-slate-200'
                      }`}>
                        {pl.active ? 'Active' : pl.isExpired ? 'Expired' : 'Revoked'}
                      </span>
                    </td>
                    <td className="p-3 text-right space-x-1.5">
                      <button
                        type="button"
                        onClick={async () => {
                          await navigator.clipboard.writeText(pl.paymentUrl);
                          notifyToast('success', 'Payment URL copied to clipboard!');
                        }}
                        className="px-2.5 py-1 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-[11px] font-bold transition-all cursor-pointer shadow-2xs"
                        title="Copy Link URL"
                      >
                        Copy URL
                      </button>

                      {pl.active && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (!(await confirmDialog(`Revoke payment link for Invoice #${pl.invoiceNumber}?\n\nOnce revoked, the link will immediately become useless.`))) return;
                            try {
                              const token = await auth.currentUser?.getIdToken();
                              const res = await wsFetch(`/api/admin/finance/invoices/${pl.invoiceId}/payment-link`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ action: 'revoke' }),
                              });
                              if (!res.ok) throw new Error('Failed to revoke payment link');
                              notifyToast('success', 'Payment link revoked successfully.');
                              fetchPaymentLinks();
                            } catch (err: any) {
                              notifyToast('error', 'Revocation Error: ' + err.message);
                            }
                          }}
                          className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg text-[11px] font-bold transition-all cursor-pointer border border-rose-200"
                          title="Revoke Payment Link Immediately"
                        >
                          Revoke Link
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {paymentLinks.length === 0 && !loadingLinks && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-400 text-xs">
                      No payment links generated yet. Generate a payment link from an invoice to view it here.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 4: PAYMENT RECEIPTS LEDGER */}
      {activeTab === 'receipts' && (
        <div className="space-y-4">
          {/* PAYMENT PENDING REVIEW — payments not yet approved or rejected */}
          {(() => {
            // Build a set of payment IDs/receipt IDs that are already resolved (Approved, Rejected, Voided)
            const resolvedKeys = new Set<string>();
            [...payments, ...receipts].forEach(rec => {
              if (rec) {
                const st = String(rec.status || '').toLowerCase().trim();
                if (['approved', 'confirmed', 'rejected', 'voided', 'void', 'refunded', 'deleted'].includes(st)) {
                  if (rec.id) resolvedKeys.add(String(rec.id));
                  if (rec.paymentId) resolvedKeys.add(String(rec.paymentId));
                  if (rec.receiptId) resolvedKeys.add(String(rec.receiptId));
                }
              }
            });

            const pendingReceipts = receipts.filter(r => {
              if (!isPendingPayment(r)) return false;
              const k1 = String(r.receiptId || '');
              const k2 = String(r.paymentId || '');
              const k3 = String(r.id || '');
              return !(resolvedKeys.has(k1) || resolvedKeys.has(k2) || resolvedKeys.has(k3));
            });

            const receiptKeys = new Set<string>();
            receipts.forEach(r => {
              if (r.receiptId) receiptKeys.add(String(r.receiptId));
              if (r.paymentId) receiptKeys.add(String(r.paymentId));
              if (r.id) receiptKeys.add(String(r.id));
            });

            const pendingPayments = payments.filter(p => {
              if (!isPendingPayment(p)) return false;
              const k1 = String(p.paymentId || '');
              const k2 = String(p.id || '');
              const k3 = String(p.receiptId || '');
              if (resolvedKeys.has(k1) || resolvedKeys.has(k2) || resolvedKeys.has(k3)) return false;
              return !(receiptKeys.has(k1) || receiptKeys.has(k2) || receiptKeys.has(k3));
            });

            const pendingList = [
              ...pendingReceipts.map(r => ({ ...r, _source: 'receipts' })),
              ...pendingPayments.map(p => ({ ...p, _source: 'payments' })),
            ].sort((a, b) => String(b.uploadedAt || b.paymentDate || '').localeCompare(String(a.uploadedAt || a.paymentDate || '')));

            // Canonical record key for server calls. Rows sourced from the
            // receipts collection must be addressed by their receipt id and
            // payments-ledger rows by their payment id — a payments row that
            // merely *references* a receipt would otherwise 404 once that
            // receipt document no longer exists.
            const refOf = (r: any) =>
              r._source === 'payments'
                ? r.paymentId || r.id || r.receiptId || ''
                : r.receiptId || r.id || r.paymentId || '';
            return (
              <div className="bg-amber-50/70 border border-amber-200 rounded-xl overflow-hidden shadow-2xs">
                <div className="p-4 border-b border-amber-100 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-600" />
                    <span className="font-extrabold text-slate-800 text-xs uppercase tracking-wider">
                      Payment Pending Review ({pendingList.length})
                    </span>
                  </div>
                  <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                    {pendingList.length === 0 ? 'All caught up' : 'Awaiting approval'}
                  </span>
                </div>
                {pendingList.length === 0 ? (
                  <div className="p-5 text-center text-xs text-slate-500">
                    No payments awaiting review. New applicant / referrer payment uploads will appear here until approved or rejected.
                  </div>
                ) : (
                  <div className="divide-y divide-amber-100">
                    {pendingList.map(r => (
                      <div key={refOf(r)} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-bold text-slate-900 text-xs">{String(refOf(r)).slice(0, 16)}</span>
                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">Pending Review</span>
                          </div>
                          <div className="text-[11px] text-slate-600 mt-0.5 truncate">
                            <span className="font-bold">{r.applicantName || 'Applicant'}</span>
                            {r.applicantEmail ? ` · ${r.applicantEmail}` : ''}
                            {r.applicantId ? ` · App ${r.applicantId}` : ''}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono font-black text-slate-900 text-sm">
                            ${fmtMoney(r.amount)} <span className="text-[10px] font-semibold text-slate-400">{r.currency || workspaceCurrency}</span>
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {r.paymentMethod || 'Proof upload'} · {r.uploadedAt || r.paymentDate ? new Date(r.uploadedAt || r.paymentDate).toLocaleDateString() : '—'}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => setSelectedPaymentDetail(r)}
                            className="px-2.5 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-lg text-[10px] font-bold transition-all cursor-pointer"
                            title="View payment detail"
                          >
                            <Eye className="h-3.5 w-3.5 inline mr-1" />View
                          </button>
                          <button
                            type="button"
                            onClick={() => handleApprovePayment(refOf(r))}
                            className="px-2.5 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white rounded-lg text-[10px] font-bold transition-all cursor-pointer"
                            title="Approve & allocate this payment"
                          >
                            <Check className="h-3.5 w-3.5 inline mr-1" />Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRejectPayment(refOf(r))}
                            className="px-2.5 py-1.5 bg-white border border-rose-200 hover:bg-rose-50 text-rose-700 rounded-lg text-[10px] font-bold transition-all cursor-pointer"
                            title="Reject this payment"
                          >
                            <X className="h-3.5 w-3.5 inline mr-1" />Reject
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePayment(refOf(r))}
                            className="px-2 py-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                            title="Permanently delete this pending payment (no credit is created)"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
            <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <span className="font-extrabold text-slate-800 text-xs uppercase tracking-wider">Payments & Receipts Collection Ledger ({receipts.length})</span>
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search payments/receipts..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-brand"
                />
              </div>
            </div>
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="p-3">Receipt Ref</th>
                  <th className="p-3">Customer / Applicant</th>
                  <th className="p-3">Description & Category</th>
                  <th className="p-3">Payment Method</th>
                  <th className="p-3">Date</th>
                  <th className="p-3 text-center">Status</th>
                  <th className="p-3 text-right">Amount</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {receipts.filter(r => {
                  if (!searchQuery.trim()) return true;
                  const q = searchQuery.toLowerCase();
                  return (
                    r.receiptId?.toLowerCase().includes(q) ||
                    r.applicantName?.toLowerCase().includes(q) ||
                    r.applicantEmail?.toLowerCase().includes(q) ||
                    r.description?.toLowerCase().includes(q) ||
                    r.category?.toLowerCase().includes(q) ||
                    String(r.amount ?? '').includes(q)
                  );
                }).slice((receiptPage - 1) * ITEMS_PER_PAGE, receiptPage * ITEMS_PER_PAGE).map(r => (
                  <tr key={r.receiptId} className="hover:bg-slate-50/80 transition-colors">
                    <td className="p-3 font-mono font-bold text-slate-900">
                      <button
                        type="button"
                        onClick={() => setSelectedPaymentDetail(r)}
                        className="hover:underline text-blue-600 hover:text-blue-800 font-bold text-xs cursor-pointer inline-flex items-center gap-1"
                        title="Click to view payment detail"
                      >
                        {r.receiptId.slice(0, 16)}
                      </button>
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => handleApplicantClick(r.applicantId, r.applicantEmail)}
                        className="text-left font-bold text-slate-900 hover:text-brand hover:underline cursor-pointer group"
                      >
                        <div>{r.applicantName || 'Applicant'}</div>
                        <div className="text-[11px] text-slate-500 group-hover:text-blue-500">{r.applicantEmail}</div>
                      </button>
                    </td>
                    <td className="p-3">
                      <div className="font-bold text-slate-800">{r.description || 'Program Payment'}</div>
                      <div className="text-[10px] text-slate-400 uppercase font-semibold">{r.category || 'General'}</div>
                    </td>
                    <td className="p-3 text-slate-600 font-semibold">{r.paymentMethod || 'Stripe / Card'}</td>
                    <td className="p-3 text-slate-500 text-[11px]">
                      {r.uploadedAt ? new Date(r.uploadedAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                        r.status === 'Approved' ? 'bg-emerald-100 text-emerald-800' :
                        r.status === 'Rejected' ? 'bg-rose-100 text-rose-800' :
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {r.status || 'Approved'}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono font-black text-slate-900">
                      ${fmtMoney(r.amount)} {r.currency || workspaceCurrency}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {(r.status === 'Pending Approval' || r.status === 'Pending') && (
                          <>
                            <button
                              title="Approve Payment"
                              onClick={() => handleApprovePayment(r.receiptId)}
                              className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold rounded-lg text-[10px] cursor-pointer"
                            >
                              Approve
                            </button>
                            <button
                              title="Reject Payment"
                              onClick={() => handleRejectPayment(r.receiptId)}
                              className="px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold rounded-lg text-[10px] cursor-pointer"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          title="Export Receipt PDF"
                          onClick={async () => {
                            try {
                              const blob = await downloadPdf(`/api/finance/receipts/${r.receiptId}/pdf`);
                              triggerBlobDownload(blob, buildApplicantPdfFileName(`Receipt-${r.receiptId}`, (r as any).applicationId || (r as any).applicantId, (r as any).applicantName));
                            } catch (err: any) {
                              notifyToast('error', err.message || 'Error exporting receipt PDF');
                            }
                          }}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                        {(r.receiptAttachment?.downloadUrl || r.attachment?.downloadUrl) && (
                          <button
                            title="View Attached Receipt / Proof"
                            onClick={() => {
                              window.open(r.receiptAttachment?.downloadUrl || r.attachment?.downloadUrl, '_blank', 'noopener');
                            }}
                            className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                          >
                            <Paperclip className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          title="Send Email Receipt"
                          onClick={async () => {
                            try {
                              const token = await auth.currentUser?.getIdToken();
                              const res = await wsFetch(`/api/admin/finance/receipts/${r.receiptId}/email`, {
                                method: 'POST',
                                headers: token ? { Authorization: `Bearer ${token}` } : {}
                              });
                              const data = await res.json().catch(() => ({}));
                              if (!res.ok) throw new Error(data.error || 'Failed to send receipt email');
                              notifyToast("success", `Payment receipt email for #${r.receiptId} sent successfully!`)
                            } catch (err: any) {
                              notifyToast('error', err.message || 'Error sending receipt email');
                            }
                          }}
                          className="p-1.5 text-slate-500 hover:text-purple-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                        >
                          <Mail className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {receipts.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-400">
                      No payment receipts logged yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {receipts.length > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500 font-medium">
                <div>
                  Showing {((receiptPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(receiptPage * ITEMS_PER_PAGE, receipts.length)} of {receipts.length} payment records
                </div>
                <div className="flex items-center gap-1">
                  <button aria-label="Previous page" title="Previous page"
                    disabled={receiptPage <= 1}
                    onClick={() => setReceiptPage(p => Math.max(1, p - 1))}
                    className="p-1.5 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-30 cursor-pointer"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="px-2 font-bold text-slate-800">Page {receiptPage} of {Math.ceil(receipts.length / ITEMS_PER_PAGE)}</span>
                  <button aria-label="Next page" title="Next page"
                    disabled={receiptPage >= Math.ceil(receipts.length / ITEMS_PER_PAGE)}
                    onClick={() => setReceiptPage(p => p + 1)}
                    className="p-1.5 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-30 cursor-pointer"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* APPROVE & ALLOCATE MODAL — pending payment approval with per-invoice allocation */}
          {isAllocationModalOpen && pendingPaymentToAllocate && (
            <Modal onClose={() => { if (!allocating) setIsAllocationModalOpen(false); }}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 space-y-4 text-xs animate-fade-in font-sans">
                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="font-extrabold text-slate-900 text-sm font-display">Approve Payment & Apply to Invoices</h3>
                    <p className="text-[10px] font-mono text-slate-400 mt-0.5">
                      #{String(pendingPaymentToAllocate.receiptId || pendingPaymentToAllocate.paymentId || pendingPaymentToAllocate.id || '').toUpperCase()}
                    </p>
                  </div>
                  <button aria-label="Close" title="Close" type="button" onClick={() => { if (!allocating) setIsAllocationModalOpen(false); }} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Payment Amount</span>
                    <span className="text-base font-black font-mono text-emerald-700">${fmtMoney(pendingPaymentToAllocate.amount)} {pendingPaymentToAllocate.currency || workspaceCurrency}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Applicant</span>
                    <span className="font-bold text-slate-900">{pendingPaymentToAllocate.applicantName || 'Applicant'}</span>
                    {pendingPaymentToAllocate.applicantEmail && (
                      <span className="block text-[11px] text-slate-500 truncate">{pendingPaymentToAllocate.applicantEmail}</span>
                    )}
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Method</span>
                    <span className="font-semibold text-slate-700">{pendingPaymentToAllocate.paymentMethod || 'Proof upload'}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Submitted</span>
                    <span className="font-semibold text-slate-700">{pendingPaymentToAllocate.uploadedAt ? new Date(pendingPaymentToAllocate.uploadedAt).toLocaleDateString() : '—'}</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-extrabold text-slate-800 text-[11px] uppercase tracking-wider">
                      Apply to Open Invoices <span className="text-slate-400">(greatest balance first)</span>
                    </span>
                    <span className={`text-[10px] font-black ${(() => {
                      const total = openInvoicesForAllocation.reduce((s, inv) => s + (Number(allocationInputs[inv.id || inv.invoiceId]) || 0), 0);
                      const payAmt = safeNum(pendingPaymentToAllocate.amount);
                      return total > payAmt + 0.001 ? 'text-rose-600' : 'text-slate-500';
                    })()}`}>
                      Allocated: ${fmtMoney(openInvoicesForAllocation.reduce((s, inv) => s + (Number(allocationInputs[inv.id || inv.invoiceId]) || 0), 0))} / ${fmtMoney(pendingPaymentToAllocate.amount)}
                    </span>
                  </div>

                  {openInvoicesForAllocation.length === 0 ? (
                    <div className="p-4 text-center text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl">
                      No open invoices found for this applicant. The full payment will be held as unapplied credit once approved.
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
                      {openInvoicesForAllocation.map(inv => {
                        const key = inv.id || inv.invoiceId;
                        const payAmt = safeNum(pendingPaymentToAllocate.amount);
                        const totalAlloc = openInvoicesForAllocation.reduce((s, i) => s + (Number(allocationInputs[i.id || i.invoiceId]) || 0), 0);
                        const maxThis = Math.min(safeNum(inv.amountDue), Math.max(0, round2(payAmt - totalAlloc + (Number(allocationInputs[key]) || 0))));
                        return (
                          <div key={key} className="px-3 py-2.5 flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-slate-900 text-xs truncate">#{inv.invoiceNumber || key}</div>
                              <div className="text-[11px] text-slate-500 truncate">{inv.programName || inv.description || 'Invoice'}</div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-[11px] text-slate-500">Due</div>
                              <div className="font-mono font-black text-slate-700 text-xs">${fmtMoney(inv.amountDue)}</div>
                            </div>
                            <div className="w-28 shrink-0">
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                max={maxThis}
                                value={allocationInputs[key] ?? 0}
                                onChange={e => {
                                  const val = Math.max(0, Number(e.target.value) || 0);
                                  setAllocationInputs(prev => ({ ...prev, [key]: round2(val) }));
                                }}
                                className="w-full px-2 py-1.5 text-xs font-mono font-bold border border-slate-200 rounded-lg outline-none focus:border-emerald-500 bg-white"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
                  <button
                    type="button"
                    disabled={allocating}
                    onClick={() => {
                      setPendingEmailPrompt({
                        isOpen: true,
                        title: 'Send Payment Allocation Confirmation Email?',
                        recipientEmail: pendingPaymentToAllocate.applicantEmail || 'Applicant',
                        attachmentsInfo: 'Payment Receipt PDF and Invoice PDF',
                        onSendNow: () => submitApproveWithAllocations(true, true),
                        onSkipEmail: () => submitApproveWithAllocations(true, false),
                      });
                    }}
                    className="flex-1 px-4 py-2.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50 text-white rounded-xl text-xs font-black transition-all cursor-pointer"
                  >
                    {allocating ? <Loader className="h-4 w-4 animate-spin inline mr-1" /> : <CheckCircle2 className="h-4 w-4 inline mr-1" />}
                    Approve & Apply Allocations
                  </button>
                  <button
                    type="button"
                    disabled={allocating}
                    onClick={() => {
                      setPendingEmailPrompt({
                        isOpen: true,
                        title: 'Send Payment Receipt Email?',
                        recipientEmail: pendingPaymentToAllocate.applicantEmail || 'Applicant',
                        attachmentsInfo: 'Payment Receipt PDF',
                        onSendNow: () => submitApproveWithAllocations(false, true),
                        onSkipEmail: () => submitApproveWithAllocations(false, false),
                      });
                    }}
                    className="px-4 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 rounded-xl text-xs font-black transition-all cursor-pointer"
                  >
                    Approve Without Allocating (Held as Credit)
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Each invoice amount is editable. Any portion not allocated remains as the applicant's unapplied credit and can be applied later.
                </p>
              </div>
            </Modal>
          )}

          {pendingEmailPrompt && pendingEmailPrompt.isOpen && (
            <Modal onClose={() => setPendingEmailPrompt(null)} ariaLabel="Confirm Email Dispatch">
              <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-md w-full space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="p-3 rounded-2xl bg-blue-50 text-blue-600">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900">{pendingEmailPrompt.title}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Target: <strong>{pendingEmailPrompt.recipientEmail}</strong>
                    </p>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 text-xs text-slate-600 space-y-1">
                  <div>• Attachments: <strong>{pendingEmailPrompt.attachmentsInfo}</strong></div>
                  <div>• Portal Links: <strong>Direct Portal & Transaction Links</strong></div>
                </div>
                <div className="flex items-center justify-end space-x-2 pt-2">
                  <button
                    onClick={() => setPendingEmailPrompt(null)}
                    className="px-3 py-2 border border-slate-200 text-xs font-bold text-slate-600 rounded-xl hover:bg-slate-50 transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => pendingEmailPrompt.onSkipEmail()}
                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer"
                  >
                    Skip Email
                  </button>
                  <button
                    onClick={() => pendingEmailPrompt.onSendNow()}
                    className="px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all cursor-pointer shadow-sm"
                  >
                    Send Now
                  </button>
                </div>
              </div>
            </Modal>
          )}
        </div>
      )}

      {/* TAB 6: QUOTES MANAGEMENT */}
      {activeTab === 'quotes' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white p-4 rounded-xl border border-slate-200">
            <div>
              <h3 className="font-extrabold text-slate-900 text-xs uppercase tracking-wider">Quotes & Estimates ({quotes.length})</h3>
              <p className="text-caption text-slate-500">Create, send, and track quotes before converting them to official invoices.</p>
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={() => { setQuoteToEdit(null); setQuoteBuilderOpen(true); }}
                className="px-3.5 py-2 bg-brand hover:bg-brand-600 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 transition-colors"
              >
                <Plus className="w-4 h-4" /> Create Quote
              </button>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-700 font-bold uppercase tracking-wider">
                <tr>
                  <th className="p-3">Quote #</th>
                  <th className="p-3">Applicant / Client</th>
                  <th className="p-3">Program & Term</th>
                  <th className="p-3">Issue / Expiration</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Total Amount</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {quotes.map(q => (
                  <tr key={q.id || q.quoteId} className="hover:bg-slate-50">
                    <td className="p-3 font-mono font-bold text-brand">{q.quoteNumber}</td>
                    <td className="p-3 font-bold text-slate-900">{q.applicantName}</td>
                    <td className="p-3 text-slate-600">{q.programName || 'N/A'} {q.term ? `(${q.term})` : ''}</td>
                    <td className="p-3 text-slate-500 font-mono text-[11px]">{q.issueDate} / {q.expirationDate}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                        q.status === 'Converted' ? 'bg-purple-100 text-purple-800' :
                        q.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {q.status}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono font-bold text-slate-900">${Number(q.total || 0).toFixed(2)}</td>
                    <td className="p-3 text-center">
                      <div className="flex justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => setSelectedQuoteForPreview(q)}
                          className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                          title="View Quote"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => { setQuoteToEdit(q); setQuoteBuilderOpen(true); }}
                          className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                          title="Edit Quote"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {quotes.length === 0 && !loadingQuotes && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-400">No quotes generated yet. Click "Create Quote" to start.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 7: AUTOMATED FINANCIAL REMINDERS */}
      {activeTab === 'reminders' && (
        <div className="space-y-6">
          <div className="bg-white p-5 rounded-xl border border-slate-200 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-900 flex items-center gap-1.5">
                  <Bell className="w-4 h-4 text-brand" /> Automated Payment Reminders
                </h3>
                <p className="text-caption text-slate-500">Configure up to 5 automated reminder schedules triggered relative to Invoice Due Date.</p>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={processingReminders}
                  onClick={async () => {
                    setProcessingReminders(true);
                    try {
                      const token = await auth.currentUser?.getIdToken();
                      const res = await safeFetch('/api/admin/finance/process-reminders', {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      notifyToast.success(`Processed reminders: ${res.processed} emails sent`);
                    } catch (err: any) {
                      notifyToast.error(err.message || 'Failed to process reminders');
                    } finally {
                      setProcessingReminders(false);
                    }
                  }}
                  className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold rounded-lg text-xs flex items-center gap-1.5"
                >
                  {processingReminders ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Run Cycle Now
                </button>

                <button
                  type="button"
                  disabled={savingReminders}
                  onClick={async () => {
                    setSavingReminders(true);
                    try {
                      const token = await auth.currentUser?.getIdToken();
                      await safeFetch('/api/admin/finance/reminders', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ rules: reminderRules }),
                      });
                      notifyToast.success('Reminder rules saved');
                    } catch (err: any) {
                      notifyToast.error(err.message || 'Failed to save rules');
                    } finally {
                      setSavingReminders(false);
                    }
                  }}
                  className="px-3.5 py-1.5 bg-brand hover:bg-brand-600 text-white font-bold rounded-lg text-xs flex items-center gap-1.5"
                >
                  {savingReminders ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save Rules
                </button>
              </div>
            </div>

            <div className="space-y-4 pt-2">
              {reminderRules.map((rule, idx) => (
                <div key={rule.id || idx} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={rule.enabled}
                        onChange={e => {
                          const copy = [...reminderRules];
                          copy[idx].enabled = e.target.checked;
                          setReminderRules(copy);
                        }}
                        className="rounded text-brand"
                      />
                      <span className="font-extrabold text-slate-800 text-xs uppercase">Reminder Slot #{idx + 1}</span>
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      <input
                        type="number"
                        min="0"
                        value={rule.days}
                        onChange={e => {
                          const copy = [...reminderRules];
                          copy[idx].days = Number(e.target.value);
                          setReminderRules(copy);
                        }}
                        className="w-16 px-2 py-1 bg-white border border-slate-300 rounded font-mono text-center font-bold"
                      />
                      <select
                        value={rule.direction}
                        onChange={e => {
                          const copy = [...reminderRules];
                          copy[idx].direction = e.target.value as any;
                          setReminderRules(copy);
                        }}
                        className="px-2 py-1 bg-white border border-slate-300 rounded font-bold"
                      >
                        <option value="before">Days Before Due Date</option>
                        <option value="on_due_date">On Due Date</option>
                        <option value="after">Days After Due Date</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Email Subject</label>
                      <input
                        type="text"
                        value={rule.subject}
                        onChange={e => {
                          const copy = [...reminderRules];
                          copy[idx].subject = e.target.value;
                          setReminderRules(copy);
                        }}
                        className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded font-medium"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Message Body Template</label>
                      <input
                        type="text"
                        value={rule.message}
                        onChange={e => {
                          const copy = [...reminderRules];
                          copy[idx].message = e.target.value;
                          setReminderRules(copy);
                        }}
                        className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded font-medium"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TAB 8: FINANCIAL REPORTS */}
      {activeTab === 'reports' && (
        <div className="space-y-6">
          {/* Multi-Select Filter Bar */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs space-y-4 text-xs">
            <div className="flex items-center justify-between">
              <h3 className="font-extrabold text-slate-900 uppercase text-xs tracking-wider flex items-center gap-2">
                <Filter className="w-4 h-4 text-brand" /> Multi-Select Report Filters
              </h3>
              <button
                type="button"
                onClick={fetchReports}
                className="px-4 py-2 bg-brand hover:bg-brand-active text-white font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer shadow-2xs"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Apply Filters & Generate
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              {/* Programs Multi-Select */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-extrabold uppercase text-slate-400 tracking-wider block">Programs</label>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 min-h-[42px] flex flex-wrap gap-1.5">
                  {reportPrograms.map(p => (
                    <span key={p} className="inline-flex items-center gap-1 bg-blue-100 text-blue-900 font-bold px-2 py-0.5 rounded-md text-[11px]">
                      {p}
                      <button type="button" onClick={() => setReportPrograms(prev => prev.filter(x => x !== p))} className="text-blue-600 hover:text-red-600 font-bold ml-1">&times;</button>
                    </span>
                  ))}
                  <div className="w-52">
                    <SearchableSelect
                      value=""
                      onChange={v => {
                        if (v && !reportPrograms.includes(v)) {
                          setReportPrograms(prev => [...prev, v]);
                        }
                      }}
                      options={availablePrograms.map(prog => ({
                        value: prog,
                        label: prog.length > 40 ? `${prog.slice(0, 39)}…` : prog,
                      }))}
                      placeholder="+ Add Program..."
                      allowClear={false}
                      emptyLabel="No programs found"
                    />
                  </div>
                </div>
              </div>

              {/* Terms Multi-Select */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-extrabold uppercase text-slate-400 tracking-wider block">Terms</label>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 min-h-[42px] flex flex-wrap gap-1.5">
                  {reportTerms.map(t => (
                    <span key={t} className="inline-flex items-center gap-1 bg-purple-100 text-purple-900 font-bold px-2 py-0.5 rounded-md text-[11px]">
                      {t}
                      <button type="button" onClick={() => setReportTerms(prev => prev.filter(x => x !== t))} className="text-purple-600 hover:text-red-600 font-bold ml-1">&times;</button>
                    </span>
                  ))}
                  <select
                    value=""
                    onChange={e => {
                      if (e.target.value && !reportTerms.includes(e.target.value)) {
                        setReportTerms(prev => [...prev, e.target.value]);
                      }
                      e.target.value = "";
                    }}
                    className="flex-1 bg-transparent border-none outline-none text-xs font-semibold text-slate-700 min-w-[100px] cursor-pointer"
                  >
                    <option value="">+ Add Term...</option>
                    {availableTerms.map(term => (
                      <option key={term} value={term}>{term}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Sponsors Multi-Select */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-extrabold uppercase text-slate-400 tracking-wider block">Sponsors</label>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 min-h-[42px] flex flex-wrap gap-1.5">
                  {reportSponsors.map(s => (
                    <span key={s} className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-900 font-bold px-2 py-0.5 rounded-md text-[11px]">
                      {s}
                      <button type="button" onClick={() => setReportSponsors(prev => prev.filter(x => x !== s))} className="text-emerald-600 hover:text-red-600 font-bold ml-1">&times;</button>
                    </span>
                  ))}
                  <div className="w-52">
                    <SearchableSelect
                      value=""
                      onChange={v => {
                        if (v && !reportSponsors.includes(v)) {
                          setReportSponsors(prev => [...prev, v]);
                        }
                      }}
                      options={availableSponsors.map(spon => ({
                        value: spon,
                        label: spon,
                      }))}
                      placeholder="+ Add Sponsor..."
                      allowClear={false}
                      emptyLabel="No sponsors found"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metric Overview Cards */}
          {reportData?.summary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Total Invoiced</span>
                <span className="text-xl font-black text-slate-900 font-mono">${reportData.summary.totalInvoiced.toFixed(2)}</span>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Total Collected</span>
                <span className="text-xl font-black text-emerald-700 font-mono">${reportData.summary.totalPaid.toFixed(2)}</span>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Total Outstanding</span>
                <span className="text-xl font-black text-amber-700 font-mono">${reportData.summary.totalOutstanding.toFixed(2)}</span>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Total Overdue</span>
                <span className="text-xl font-black text-red-700 font-mono">${reportData.summary.totalOverdue.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Collection Schedule View */}
          {reportData?.collectionSchedule && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs space-y-4 p-5">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <h3 className="font-extrabold text-slate-900 text-xs uppercase tracking-wider flex items-center gap-2">
                  <Clock className="w-4 h-4 text-brand" /> Collection Schedule & Aging Breakdown
                </h3>
                <span className="text-xs font-semibold text-slate-500">
                  Active Invoice Schedules
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {Object.entries(reportData.collectionSchedule).map(([key, sched]: [string, any]) => (
                  <div key={key} className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-1">
                    <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider block">{sched.label}</span>
                    <div className="text-lg font-black font-mono text-slate-900">${Number(sched.total || 0).toFixed(2)}</div>
                    <span className="text-[11px] font-semibold text-slate-600 block">{sched.count} Invoices</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 9: FINANCIAL AUDIT TRAIL */}
      {activeTab === 'audit' && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center">
              <h3 className="font-extrabold text-slate-800 text-xs uppercase tracking-wider flex items-center gap-1.5">
                <History className="w-4 h-4 text-brand" /> Financial Activity Audit Logs
              </h3>
              <button
                type="button"
                onClick={fetchAuditLogs}
                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg text-xs font-bold"
              >
                Refresh Log
              </button>
            </div>

            <table className="w-full text-left text-xs">
              <thead className="bg-slate-100/80 border-b border-slate-200 text-slate-700 font-extrabold uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="p-3.5">Date / Time</th>
                  <th className="p-3.5">Performed By (Actor)</th>
                  <th className="p-3.5">Recipient (To)</th>
                  <th className="p-3.5">Action</th>
                  <th className="p-3.5">Entity & ID</th>
                  <th className="p-3.5">Amount ($)</th>
                  <th className="p-3.5">Payment Link</th>
                  <th className="p-3.5">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-medium text-slate-700">
                {auditLogs.map(log => {
                  const recipient = log.recipient || log.metadata?.recipient || log.metadata?.to || log.metadata?.applicantEmail || log.metadata?.email || "-";
                  const payLink = log.paymentLink || log.metadata?.paymentLink || log.metadata?.paymentUrl || "";
                  return (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3.5 font-mono text-slate-500 text-[11px] whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                      <td className="p-3.5 font-bold text-slate-900 whitespace-nowrap">{log.actor}</td>
                      <td className="p-3.5 font-semibold text-slate-800 whitespace-nowrap">{recipient}</td>
                      <td className="p-3.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black bg-blue-50 text-blue-700 border border-blue-200">
                          {log.action}
                        </span>
                      </td>
                      <td className="p-3.5 font-mono text-slate-600 whitespace-nowrap">{log.entityType} #{log.entityId}</td>
                      <td className="p-3.5 font-mono font-bold text-slate-900 whitespace-nowrap">${Number(log.amount || 0).toFixed(2)}</td>
                      <td className="p-3.5 whitespace-nowrap">
                        {payLink ? (
                          <a href={payLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 font-bold hover:underline text-[11px]">
                            View Link &rarr;
                          </a>
                        ) : (
                          <span className="text-slate-400 text-[11px]">—</span>
                        )}
                      </td>
                      <td className="p-3.5 text-slate-600 max-w-xs truncate" title={log.details}>{log.details || "-"}</td>
                    </tr>
                  );
                })}
                {auditLogs.length === 0 && !loadingAuditLogs && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-slate-400">No financial audit logs recorded yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* INVOICE BUILDER MODAL */}
      <InvoiceBuilderModal
        isOpen={isInvoiceBuilderOpen}
        onClose={() => { setIsInvoiceBuilderOpen(false); setEditingInvoice(null); }}
        onSave={handleSaveInvoice}
        existingInvoice={editingInvoice}
        applications={applications}
        products={products}
      />

      {/* INVOICE PREVIEW MODAL */}
      <InvoicePreviewModal
        isOpen={!!previewInvoice}
        onClose={() => setPreviewInvoice(null)}
        invoice={previewInvoice}
        branding={branding}
        financeSettings={{ billingEmail, invoiceFooter }}
        onSendEmail={handleSendInvoiceEmail}
        onRecordPayment={(inv) => {
          setPaymentTargetInvoice(inv);
          setPaymentAmount(inv.amountDue.toString());
          setIsRecordPaymentOpen(true);
        }}
        onDisassociatePayment={handleDisassociateAllocation}
        onOpenPaymentDetail={(pId) => {
          const found = receipts.find((r: any) => r.receiptId === pId || r.id === pId);
          if (found) {
            setSelectedPaymentDetail(found);
          } else {
            setSelectedPaymentDetail({ receiptId: pId, paymentId: pId, amount: 0, currency: 'USD' });
          }
        }}
        isAdminView={true}
      />

      {/* PAYMENT DETAIL MODAL IN FINANCE TAB */}
      {selectedPaymentDetail && (
        <Modal onClose={() => setSelectedPaymentDetail(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 text-xs animate-fade-in font-sans">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-extrabold text-slate-900 text-sm font-display">Payment Details & Receipt</h3>
                <p className="text-[10px] font-mono text-slate-400 mt-0.5">#{String(selectedPaymentDetail.receiptId || selectedPaymentDetail.paymentId || selectedPaymentDetail.id || '').toUpperCase()}</p>
              </div>
              <button aria-label="Close" title="Close" type="button" onClick={() => setSelectedPaymentDetail(null)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Amount Paid</span>
                  <span className="text-base font-black font-mono text-slate-900">${fmtMoney(selectedPaymentDetail.amount)} {selectedPaymentDetail.currency || workspaceCurrency}</span>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Unapplied Balance</span>
                  <span className="text-base font-black font-mono text-purple-700">${fmtMoney(selectedPaymentDetail.unappliedBalance ?? (selectedPaymentDetail.amount - (selectedPaymentDetail.totalAllocated || 0)))}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-slate-700">
                <div><span className="font-bold text-slate-400">Customer:</span> {selectedPaymentDetail.applicantName || 'Applicant'}</div>
                <div><span className="font-bold text-slate-400">Email:</span> {selectedPaymentDetail.applicantEmail || '-'}</div>
                <div><span className="font-bold text-slate-400">Payment Date:</span> {selectedPaymentDetail.paymentDate ? new Date(selectedPaymentDetail.paymentDate).toLocaleDateString() : '-'}</div>
                <div><span className="font-bold text-slate-400">Method:</span> {selectedPaymentDetail.paymentMethod || 'Credit Card'}</div>
                <div><span className="font-bold text-slate-400">Reference:</span> {selectedPaymentDetail.paymentReference || '-'}</div>
                <div><span className="font-bold text-slate-400">Status:</span> <span className="font-bold uppercase text-blue-600">{selectedPaymentDetail.status || 'Approved'}</span></div>
              </div>

              {selectedPaymentDetail.notes && (
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-slate-700">
                  <span className="font-bold text-slate-400 block text-[10px] uppercase">Notes:</span>
                  {selectedPaymentDetail.notes}
                </div>
              )}

              {selectedPaymentDetail.rejectionReason && (
                <div className="bg-rose-50 p-2.5 rounded-lg border border-rose-200 text-rose-800">
                  <span className="font-bold block text-[10px] uppercase">Rejection Reason:</span>
                  {selectedPaymentDetail.rejectionReason}
                </div>
              )}

              {Array.isArray(selectedPaymentDetail.transactions) && selectedPaymentDetail.transactions.length > 0 && (
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                  <span className="font-bold text-slate-400 block text-[10px] uppercase mb-2">Transaction History</span>
                  <div className="space-y-1.5">
                    {[...selectedPaymentDetail.transactions].reverse().map((t: any, idx: number) => (
                      <div key={idx} className="flex items-start justify-between gap-2 text-[11px]">
                        <div className="min-w-0">
                          <span className={`font-extrabold uppercase ${t.action === 'OVERPAYMENT' || t.action === 'REMOVED_FROM_INVOICE' || t.action === 'DISASSOCIATED' ? 'text-purple-700' : t.action === 'ALLOCATED' || t.action === 'CREDIT_APPLIED' ? 'text-emerald-700' : 'text-slate-700'}`}>
                            {String(t.action || 'EVENT').replace(/_/g, ' ')}
                          </span>
                          <span className="text-slate-500 block truncate">{t.note || (t.invoiceNumber ? `Invoice #${t.invoiceNumber}` : '')}</span>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono font-bold text-slate-900">${fmtMoney(t.amount)}</div>
                          <div className="text-slate-400 text-[10px]">{t.at ? new Date(t.at).toLocaleString() : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(selectedPaymentDetail.status === 'Pending Approval' || selectedPaymentDetail.status === 'Pending') && (
                <div className="bg-amber-50 p-3.5 rounded-xl border border-amber-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black uppercase text-amber-900 tracking-wider">Pending Review</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-amber-200 text-amber-900">Action Needed</span>
                  </div>
                  <p className="text-[11px] text-amber-800">
                    Approving this payment will confirm the funds and automatically apply them to the target invoice.
                  </p>
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => handleApprovePayment(selectedPaymentDetail.receiptId || selectedPaymentDetail.paymentId)}
                      className="flex-1 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white font-extrabold rounded-lg text-xs transition-all cursor-pointer shadow-2xs"
                    >
                      Approve Payment
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRejectPayment(selectedPaymentDetail.receiptId || selectedPaymentDetail.paymentId)}
                      className="flex-1 py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-extrabold rounded-lg text-xs transition-all cursor-pointer shadow-2xs"
                    >
                      Reject Payment
                    </button>
                  </div>
                </div>
              )}

              {(selectedPaymentDetail.downloadUrl || selectedPaymentDetail.storagePath) && (
                <div className="bg-blue-50/50 p-3 rounded-xl border border-blue-100 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-bold uppercase text-blue-800 block">Proof of Payment Attached</span>
                    <span className="text-xs text-slate-600 truncate block max-w-[200px]">{selectedPaymentDetail.fileName || 'Attachment'}</span>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const token = await auth.currentUser?.getIdToken();
                        const p = selectedPaymentDetail.storagePath
                          ? `/api/serve-inline?path=${encodeURIComponent(selectedPaymentDetail.storagePath)}`
                          : selectedPaymentDetail.downloadUrl;
                        const res = await fetch(p, {
                          headers: token && selectedPaymentDetail.storagePath ? { Authorization: `Bearer ${token}` } : {}
                        });
                        if (!res.ok) throw new Error('Failed to fetch file');
                        const blob = await res.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        window.open(blobUrl, '_blank');
                      } catch (e: any) {
                        notifyToast('error', e.message || 'Error opening file');
                      }
                    }}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs transition-all flex items-center gap-1 cursor-pointer"
                  >
                    <Eye className="h-3.5 w-3.5" /><span>View File</span>
                  </button>
                </div>
              )}
            </div>

            <div className="pt-3 flex justify-between items-center border-t border-slate-100">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const recId = selectedPaymentDetail.receiptId || selectedPaymentDetail.paymentId || selectedPaymentDetail.id;
                      const blob = await downloadPdf(`/api/finance/receipts/${recId}/pdf`);
                      triggerBlobDownload(blob, buildApplicantPdfFileName(`Receipt-${recId}`, selectedPaymentDetail.applicationId || selectedPaymentDetail.applicantId, selectedPaymentDetail.applicantName));
                    } catch (err: any) {
                      notifyToast('error', err.message || 'Error exporting receipt PDF');
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs text-xs"
                >
                  <Download className="h-3.5 w-3.5" /><span>Export PDF</span>
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const token = await auth.currentUser?.getIdToken();
                      const recId = selectedPaymentDetail.receiptId || selectedPaymentDetail.paymentId || selectedPaymentDetail.id;
                      const res = await wsFetch(`/api/admin/finance/receipts/${recId}/email`, {
                        method: 'POST',
                        headers: token ? { Authorization: `Bearer ${token}` } : {}
                      });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) throw new Error(data.error || 'Failed to send receipt email');
                      notifyToast('success', 'Payment receipt email with attached PDF sent successfully!');
                    } catch (err: any) {
                      notifyToast('error', err.message || 'Error sending receipt email');
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs text-xs"
                >
                  <Mail className="h-3.5 w-3.5" /><span>Email Receipt</span>
                </button>
              </div>

              <div className="flex flex-col gap-2">
                {safeNum(selectedPaymentDetail.totalAllocated) > 0 && (
                  <button
                    type="button"
                    onClick={() => handleRemovePaymentFromInvoice(selectedPaymentDetail.paymentId || selectedPaymentDetail.id)}
                    className="px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold rounded-xl transition-all cursor-pointer text-xs text-left"
                    title="Keep the payment but return its funds to unapplied credit"
                  >
                    Remove from Invoice
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleDeletePayment(selectedPaymentDetail.paymentId || selectedPaymentDetail.id)}
                  className="px-4 py-2 bg-rose-100 hover:bg-rose-200 text-rose-900 font-bold rounded-xl transition-all cursor-pointer text-xs text-left"
                >
                  Delete Payment
                </button>
              </div>

              <button
                type="button"
                onClick={() => setSelectedPaymentDetail(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all cursor-pointer text-xs"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* RECORD MANUAL PAYMENT MODAL */}
      {isRecordPaymentOpen && paymentTargetInvoice && (
        <Modal onClose={() => setIsRecordPaymentOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4 text-xs animate-fade-in">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-extrabold text-slate-900 text-sm font-display">Record Payment for #{paymentTargetInvoice.invoiceNumber}</h3>
              <button aria-label="Close" title="Close" onClick={() => { setIsRecordPaymentOpen(false); setPaymentReceiptFile(null); }} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Payment Amount ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={paymentAmount}
                  onChange={e => setPaymentAmount(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold font-mono text-slate-900 outline-none focus:bg-white"
                />
                <span className="text-[11px] text-slate-500 mt-0.5 block">
                  Balance due: ${safeNum(paymentTargetInvoice.amountDue ?? (paymentTargetInvoice.total ?? paymentTargetInvoice.amount)).toFixed(2)} &bull; Overpayments are held as applicant credit
                </span>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={e => setPaymentMethod(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-semibold text-slate-800 outline-none"
                >
                  <option value="Credit Card / Stripe">Credit Card / Stripe</option>
                  <option value="Bank Wire / ACH">Bank Wire / ACH</option>
                  <option value="Cash / Manual">Cash / Manual</option>
                  <option value="Scholarship / Grant">Scholarship / Grant</option>
                </select>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Transaction Ref / Cheque #</label>
                <input
                  type="text"
                  placeholder="e.g. TXN-998231"
                  value={paymentReference}
                  onChange={e => setPaymentReference(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-semibold text-slate-800 outline-none"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Payment Notes</label>
                <textarea
                  rows={2}
                  placeholder="Optional internal remarks..."
                  value={paymentNotes}
                  onChange={e => setPaymentNotes(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-medium text-slate-800 outline-none resize-none"
                />
              </div>

              <div className="pt-1">
                <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-bold text-slate-700">
                  <input
                    type="checkbox"
                    checked={emailReceiptToApplicant}
                    onChange={e => setEmailReceiptToApplicant(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer"
                  />
                  <span>Email receipt to applicant ({paymentTargetInvoice.applicantEmail || 'applicant'})</span>
                </label>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Attach Receipt / Proof of Payment (optional)</label>
                <div className="border-2 border-dashed border-slate-300 bg-slate-50 hover:border-blue-500 rounded-lg p-3 text-center cursor-pointer transition-all">
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    onChange={e => setPaymentReceiptFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="record-payment-receipt-input"
                  />
                  <label htmlFor="record-payment-receipt-input" className="cursor-pointer block space-y-1">
                    <Upload className="h-4 w-4 text-slate-400 mx-auto" />
                    <span className="text-[11px] font-bold text-slate-700 block truncate px-2">
                      {paymentReceiptFile ? paymentReceiptFile.name : 'Click to attach receipt file'}
                    </span>
                    <span className="text-[11px] text-slate-500 block">PDF or image, up to 10MB</span>
                  </label>
                  {paymentReceiptFile && (
                    <button
                      type="button"
                      onClick={() => setPaymentReceiptFile(null)}
                      className="mt-1 text-[10px] font-bold text-rose-600 hover:text-rose-700 cursor-pointer"
                    >
                      Remove attachment
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => { setIsRecordPaymentOpen(false); setPaymentReceiptFile(null); }}
                className="px-3 py-1.5 border border-slate-200 text-slate-600 font-bold rounded-lg cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isRecordingPayment || !paymentAmount}
                onClick={handleRecordPaymentSubmit}
                className="px-4 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white font-bold rounded-lg cursor-pointer disabled:opacity-50"
              >
                {isRecordingPayment ? 'Recording...' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* CREATE / EDIT PRODUCT MODAL */}
      {isProductModalOpen && (
        <ProductFormModal
          isOpen={isProductModalOpen}
          onClose={() => setIsProductModalOpen(false)}
          onSave={handleSaveProduct}
          existingProduct={editingProduct}
        />
      )}

      {/* FINANCE SETTINGS MODAL */}
      {isSettingsOpen && (
        <Modal onClose={() => setIsSettingsOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 space-y-6 text-xs animate-fade-in">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-brand" />
                <h3 className="font-extrabold text-slate-900 text-sm font-display">Workspace Finance Settings & Templates</h3>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setIsSettingsOpen(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSaveSettings} className="space-y-4">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Payment / Billing Contact Email *</label>
                <input
                  type="email"
                  required
                  value={billingEmail}
                  onChange={e => setBillingEmail(e.target.value)}
                  placeholder="e.g. billing@edutrouve.org"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold text-slate-900 outline-none focus:bg-white"
                />
                <span className="text-[11px] text-slate-500 mt-1 block">Displayed on invoice headers and payment receipts.</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Workspace Base Currency *</label>
                  <select
                    value={workspaceCurrency}
                    onChange={e => setWorkspaceCurrency(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold text-slate-900 outline-none focus:bg-white"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="CAD">CAD ($)</option>
                    <option value="AUD">AUD ($)</option>
                    <option value="ZAR">ZAR (R)</option>
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Default Payment Terms</label>
                  <input
                    type="text"
                    value={defaultTerms}
                    onChange={e => setDefaultTerms(e.target.value)}
                    placeholder="e.g. Net 14 Days"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-semibold text-slate-800 outline-none focus:bg-white"
                  />
                </div>
              </div>

              {/* Workspace Late Fee Defaults */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-extrabold text-xs text-slate-800 uppercase tracking-wider block">Default Late Fee for Workspace Invoices</span>
                    <span className="text-[11px] text-slate-500">Default settings applied to new workspace invoices</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={lateFeeDefaultEnabled}
                      onChange={(e) => setLateFeeDefaultEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-600"></div>
                    <span className="ml-2 text-xs font-bold text-slate-700">{lateFeeDefaultEnabled ? 'Enabled' : 'Disabled'}</span>
                  </label>
                </div>

                {lateFeeDefaultEnabled && (
                  <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-200 text-xs">
                    <div>
                      <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Late Fee Type</label>
                      <select
                        value={lateFeeDefaultType}
                        onChange={(e) => setLateFeeDefaultType(e.target.value as any)}
                        className="w-full bg-white border border-slate-200 rounded-lg p-2 font-bold text-slate-800 outline-none"
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Dollar ($)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Late Fee Value</label>
                      <input
                        type="number"
                        min="0"
                        value={lateFeeDefaultValue}
                        onChange={(e) => setLateFeeDefaultValue(parseFloat(e.target.value) || 0)}
                        className="w-full bg-white border border-slate-200 rounded-lg p-2 font-bold font-mono text-slate-900 outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Grace Period (Days)</label>
                      <input
                        type="number"
                        min="1"
                        value={lateFeeDefaultDays}
                        onChange={(e) => setLateFeeDefaultDays(parseInt(e.target.value) || 1)}
                        className="w-full bg-white border border-slate-200 rounded-lg p-2 font-bold font-mono text-slate-900 outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div>
                    <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-800 block">Global Invoice Footer (Rich Text / Table Support)</label>
                    <p className="text-[11px] text-slate-500 font-medium">Compose global footer disclaimers, payment instructions, banking details, or itemized policy tables.</p>
                  </div>
                </div>
                <div className="border border-slate-300 rounded-xl overflow-hidden bg-white shadow-2xs min-h-[220px]">
                  <TiptapEditor
                    html={invoiceFooter}
                    onChange={html => setInvoiceFooter(html)}
                    placeholder="Enter global invoice and receipt footer notes, banking terms, or tables..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsSettingsOpen(false)}
                  className="px-3 py-1.5 border border-slate-200 text-slate-600 font-bold rounded-lg cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingSettings}
                  className="px-4 py-1.5 btn-primary text-white font-bold rounded-lg cursor-pointer disabled:opacity-50"
                >
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>

            {/* Reusable Templates Section */}
            <div className="pt-4 border-t border-slate-200 space-y-3">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <h4 className="text-xs font-black uppercase text-slate-900 tracking-wider">Reusable Invoice & Quote Templates</h4>
                  <p className="text-[11px] text-slate-500">Configure standard line items, rates, and 6-digit template codes to generate invoices or quotes.</p>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <input
                    type="text"
                    placeholder="Search templates by code or name..."
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-800 outline-none focus:border-brand w-full sm:w-56"
                  />
                  <button
                    type="button"
                    onClick={() => { setEditingTemplate(null); setIsTemplateModalOpen(true); }}
                    className="px-3 py-1.5 bg-brand hover:bg-brand-active text-white font-bold rounded-lg text-xs transition-all cursor-pointer flex items-center gap-1 shrink-0"
                  >
                    <Plus className="h-3.5 w-3.5" /> New Template
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {templates
                  .filter((t) => {
                    if (!templateSearch.trim()) return true;
                    const query = templateSearch.toLowerCase().trim();
                    return (
                      searchText(t.name).toLowerCase().includes(query) ||
                      searchText(t.code).toLowerCase().includes(query) ||
                      searchText(t.description).toLowerCase().includes(query)
                    );
                  })
                  .map(tmpl => {
                    const typesDisplay = Array.isArray(tmpl.types)
                      ? tmpl.types.join(' & ')
                      : (tmpl.type || 'invoice');
                    return (
                      <div key={tmpl.id} className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                          {tmpl.code && (
                            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded font-mono font-bold text-[11px] shrink-0">
                              {tmpl.code}
                            </span>
                          )}
                          <div className="min-w-0">
                            <div className="font-bold text-slate-900 text-xs truncate">
                              {tmpl.name} <span className="text-[10px] font-normal text-slate-400 capitalize">({typesDisplay})</span>
                            </div>
                            <div className="text-[11px] text-slate-500 truncate">
                              {tmpl.description || 'No description'} &bull; {tmpl.items?.length || 0} pre-loaded items &bull; Total: ${(tmpl.total || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-2">
                          <button
                            onClick={() => { setEditingTemplate(tmpl); setIsTemplateModalOpen(true); }}
                            className="p-1.5 text-slate-400 hover:text-blue-600 rounded-lg cursor-pointer"
                            title="Edit Template"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(tmpl.id)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg cursor-pointer"
                            title="Delete Template"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                {templates.length === 0 && (
                  <div className="p-4 text-center text-slate-400 text-xs italic">No templates created yet. Click "New Template" to create one.</div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* CREATE / EDIT TEMPLATE MODAL */}
      {isTemplateModalOpen && (
        <InvoiceTemplateModal
          isOpen={isTemplateModalOpen}
          onClose={() => { setIsTemplateModalOpen(false); setEditingTemplate(null); }}
          onSave={handleSaveTemplatePayload}
          existingTemplate={editingTemplate}
          products={products}
          programs={programCatalog}
          sponsors={sponsorCatalog}
        />
      )}

      {/* Quote Modals */}
      <QuoteBuilderModal
        isOpen={quoteBuilderOpen}
        onClose={() => { setQuoteBuilderOpen(false); setQuoteToEdit(null); }}
        quoteToEdit={quoteToEdit}
        applications={applications}
        products={products}
        onQuoteSaved={fetchQuotes}
      />

      <QuotePreviewModal
        isOpen={!!selectedQuoteForPreview}
        onClose={() => setSelectedQuoteForPreview(null)}
        quote={selectedQuoteForPreview}
        branding={branding}
        onQuoteUpdated={fetchQuotes}
        onViewInvoice={id => {
          const matchedInv = invoices.find(i => i.id === id || i.invoiceId === id);
          if (matchedInv) setPreviewInvoice(matchedInv);
        }}
      />
    </div>
  );
}

// Subcomponent: Product Form Modal
function ProductFormModal({
  isOpen,
  onClose,
  onSave,
  existingProduct,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<FinanceProduct>) => Promise<void>;
  existingProduct: FinanceProduct | null;
}) {
  useBodyScrollLock(isOpen);

  const [name, setName] = useState(existingProduct?.name || '');
  const [description, setDescription] = useState(existingProduct?.description || '');
  const [category, setCategory] = useState(existingProduct?.category || 'Program Fee');
  const [costPrice, setCostPrice] = useState(existingProduct?.costPrice?.toString() || '0');
  const [sellingPrice, setSellingPrice] = useState(existingProduct?.sellingPrice?.toString() || '0');
  const [productId, setProductId] = useState(existingProduct?.productId || Math.floor(10000 + Math.random() * 90000).toString());
  const [taxRate, setTaxRate] = useState(existingProduct?.taxRate?.toString() || '0');
  const [isActive, setIsActive] = useState(existingProduct?.isActive ?? true);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !sellingPrice) return;
    setSaving(true);
    try {
      await onSave({
        id: existingProduct?.id,
        productId: productId.trim() || Math.floor(10000 + Math.random() * 90000).toString(),
        name: name.trim(),
        description: description.trim(),
        category,
        costPrice: parseFloat(costPrice || '0'),
        sellingPrice: parseFloat(sellingPrice || '0'),
        taxRate: parseFloat(taxRate || '0'),
        isActive,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 text-xs animate-fade-in">
        <div className="flex justify-between items-center border-b border-slate-100 pb-3">
          <h3 className="font-extrabold text-slate-900 text-sm font-display">
            {existingProduct ? 'Edit Billable Product' : 'Add Billable Product'}
          </h3>
          <button aria-label="Close" title="Close" type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Product / Item Name *</label>
            <input
              type="text"
              required
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. J-1 Visa Sponsorship Application Fee"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold text-slate-900 outline-none focus:bg-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Category</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-semibold text-slate-800 outline-none"
              >
                <option value="Program Fee">Program Fee</option>
                <option value="Visa Fee">Visa Fee</option>
                <option value="Insurance">Insurance</option>
                <option value="Accommodation">Accommodation</option>
                <option value="Flight">Flight</option>
                <option value="Admin Fee">Admin Fee</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Product ID (5 Digits)</label>
              <input
                type="text"
                required
                maxLength={10}
                value={productId}
                onChange={e => setProductId(e.target.value)}
                placeholder="e.g. 12345"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-mono font-bold text-slate-900 outline-none focus:bg-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Selling Price *</label>
              <input
                type="number"
                step="0.01"
                required
                value={sellingPrice}
                onChange={e => setSellingPrice(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold font-mono text-slate-900 outline-none focus:bg-white"
              />
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Internal Cost Price (Admin Only)</label>
              <input
                type="number"
                step="0.01"
                value={costPrice}
                onChange={e => setCostPrice(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold font-mono text-slate-700 outline-none focus:bg-white"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Description</label>
            <textarea
              rows={2}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Detailed description of what this product includes..."
              className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-medium text-slate-800 outline-none resize-none"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
              className="rounded text-brand"
            />
            <label htmlFor="isActive" className="font-bold text-slate-700 cursor-pointer">Active in Product Catalogue</label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 border border-slate-200 text-slate-600 font-bold rounded-lg cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-1.5 bg-brand hover:bg-brand-active text-white font-bold rounded-lg cursor-pointer disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Product'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
